import * as THREE from 'three';
import { EquipSlot } from './constants';
import {
  buildBaseCharacterSignature,
  resolveBaseCharacterFaceColorOverrides,
  resolveBaseCharacterColorSwaps,
  type BaseCharacterAppearance,
} from './baseCharacter';
import {
  colorKey,
  createColorRemap,
  createHslRemap,
  hslToRgbNormalized,
  type CharacterColorSwap,
} from './characterColorRemap';

// Re-exported so existing callers of `@/shell/characterAppearance` keep working.
export { hslToRgbNormalized };

export interface CharacterMeshAnimationData {
  baseNativePositions: Float32Array;
  workingNativePositions: Float32Array;
  expandedVertexSources: Uint32Array;
  renderPositionNudges?: Float32Array;
  smoothNormals?: boolean;
  radialOutset?: number;
  radialOutsetTopY?: number;
  radialOutsetHeight?: number;
  radialOutsetMinFactor?: number;
  positionAttribute: THREE.BufferAttribute;
  geometry: THREE.BufferGeometry;
  vertexGroups: number[][];
  pivotSource: boolean;
  appearanceSlot?: number;
  equipmentSlot?: number;
  equipmentName?: string;
}

export interface CharacterModelEntry {
  id: number;
  vertices: number[];
  indices: number[];
  colors: number[];
  /** Per-face raw HSL16 keys. Present on models extracted by the post-fix
   *  RSPK pipeline; preferred for recolor matching when available. */
  faceColors?: number[];
  faceTextures?: number[];
  faceTextureUCoordinates?: number[][];
  faceTextureVCoordinates?: number[][];
  faceRenderPriorities?: number[];
  faceRenderTypes?: number[];
  alphas?: number[];
  vertexGroups?: number[][];
  animayaGroups?: number[][];
  animayaScales?: number[][];
}

interface CharacterAppearanceSource {
  bodyModelKeys: string[];
  dialogueModelKeys: string[];
  translationY: number;
  translation?: {
    x: number;
    y: number;
    z: number;
  };
}

interface CharacterKitEntry {
  id: number;
  gender: 'male' | 'female' | 'unknown';
  appearanceSlot: number;
  bodyModelKeys: string[];
  recolors?: CharacterColorSwap[];
}

interface CharacterItemAppearance {
  id: number;
  source: string;
  name: string;
  male: CharacterAppearanceSource;
  female: CharacterAppearanceSource;
  slot: number | null;
  equipmentType?: string | null;
  doubleHanded?: boolean;
  weaponInterface?: string | null;
  recolors?: CharacterColorSwap[];
  wearPos?: {
    primary: number | null;
    secondary: number | null;
    tertiary: number | null;
  } | null;
}

interface CharacterAppearanceManifest {
  defaults: {
    male: {
      kitByAppearanceSlot: Record<string, number>;
    };
  };
  kits: CharacterKitEntry[];
  items: Record<string, CharacterItemAppearance>;
}

export interface CharacterAppearanceAssets {
  manifest: CharacterAppearanceManifest;
  models: Record<string, CharacterModelEntry>;
}

export interface EquippedAppearanceItem {
  id: number;
  slot: number;
}

export interface CharacterAnimationTransform {
  type: number;
  groups: number[];
  dx: number;
  dy: number;
  dz: number;
}

export interface CharacterAnimationFrame {
  length: number;
  transforms: CharacterAnimationTransform[];
}

export interface CharacterAnimationSequence {
  id: number;
  frameCount: number;
  totalLength: number;
  frames: CharacterAnimationFrame[];
}

interface MaterialStyle {
  roughness: number;
  metalness: number;
  emissiveIntensity: number;
  doubleSided?: boolean;
  polygonOffset?: boolean;
  polygonOffsetFactor?: number;
  polygonOffsetUnits?: number;
  nudgeAllFaces?: boolean;
  nudgeFaceColors?: number[];
  nudgeFlatOverlayFaces?: boolean;
  faceNudgeAmount?: number;
  flatOverlayFaceNudgeAmount?: number;
  nudgePriorityFaces?: boolean;
  priorityFaceNudgeAmount?: number;
  capeEmblemOverlayFaces?: boolean;
  capeEmblemOverlayNudgeAmount?: number;
  smoothNormals?: boolean;
  infernalLavaPattern?: boolean;
  proceduralTexture?: 'infernal-cape';
  depthFillLowerCape?: boolean;
  skipLargeCapeArtifactFaces?: boolean;
  faceColorOverrides?: Record<number, [number, number, number]>;
  texturePaths?: Record<number, string>;
  skipUntexturedFaceColors?: number[];
  depthTest?: boolean;
  depthWrite?: boolean;
  textureDepthWrite?: boolean;
  radialOutset?: number;
  radialOutsetTopY?: number;
  radialOutsetHeight?: number;
  radialOutsetMinFactor?: number;
}

type AnimationMode = 'full' | 'rigid';
type AttachmentSide = 'negative-x' | 'positive-x';
export type CharacterAnimationStrategy =
  | 'current'
  | 'body-only'
  | 'armor-deform'
  | 'armor-deform-all-pivots'
  | 'full-if-rigged'
  | 'static-pose';

interface CharacterAssemblyOptions {
  animationStrategy?: CharacterAnimationStrategy;
  presentationOverrides?: Record<string, EquipmentPresentationOverride>;
  itemOnly?: boolean;
}

export interface EquipmentPresentationOverride {
  offset?: [number, number, number];
  nativeOffset?: [number, number, number];
  nativeMirror?: [boolean, boolean, boolean];
  nativeRotation?: [number, number, number];
  rotation?: [number, number, number];
  mode?: 'full' | 'rigid';
  standAnimation?: number;
}

interface RigidAttachmentState {
  slot: number | null;
  anchorSlots: number[];
  aimSlots: number[];
  anchorSide?: AttachmentSide;
  aimSide?: AttachmentSide;
  restPosition: [number, number, number];
  restQuaternion: [number, number, number, number];
  restAnchor?: [number, number, number];
  restAim?: [number, number, number];
}

interface AppearanceSlotCenterMaps {
  all: Map<number, THREE.Vector3>;
  negativeX: Map<number, THREE.Vector3>;
  positiveX: Map<number, THREE.Vector3>;
}

const MODEL_UNIT_SCALE = 1 / 128;
const EQUIPMENT_SLOTS = [
  EquipSlot.HEAD,
  EquipSlot.CAPE,
  EquipSlot.NECK,
  EquipSlot.WEAPON,
  EquipSlot.BODY,
  EquipSlot.SHIELD,
  EquipSlot.LEGS,
  EquipSlot.HANDS,
  EquipSlot.FEET,
  EquipSlot.RING,
  EquipSlot.AMMO,
] as const;
const VISIBLE_RENDER_SLOTS = new Set<number>([
  EquipSlot.HEAD,
  EquipSlot.CAPE,
  EquipSlot.NECK,
  EquipSlot.WEAPON,
  EquipSlot.BODY,
  EquipSlot.SHIELD,
  EquipSlot.LEGS,
  EquipSlot.HANDS,
  EquipSlot.FEET,
]);
const BASE_KIT_MATERIAL: MaterialStyle = {
  roughness: 1,
  metalness: 0,
  emissiveIntensity: 0.02,
};
const SKILLCAPE_SHARED_BODY_HSL = new Set([54503, 57280, 54183]);
const EQUIPMENT_TEXTURE_CACHE = new Map<string, THREE.Texture>();
const DEFAULT_MIN_EQUIPMENT_RIG_GROUPS = 6;
const MIN_BASE_KIT_RIG_GROUPS = 1;
const MIN_EQUIPMENT_RIG_GROUPS_BY_SLOT: Partial<Record<number, number>> = {
  [EquipSlot.HEAD]: 1,
  // Cape trim/icon geometry can be sparse, but its vertex groups still match
  // the cape swing rig. Keep the threshold low so every cape layer follows.
  [EquipSlot.CAPE]: 1,
  [EquipSlot.NECK]: 2,
  [EquipSlot.WEAPON]: 1,
  // Split torso/body pieces like Verac's brassard and Karil's leathertop
  // can have only 3-4 groups per sub-model while still using valid pivots.
  [EquipSlot.BODY]: 2,
  [EquipSlot.SHIELD]: 8,
  [EquipSlot.LEGS]: 10,
  [EquipSlot.HANDS]: 4,
  [EquipSlot.FEET]: 6,
};
const NATIVE_POSE_EQUIPMENT_SLOTS = new Set<number>([
  EquipSlot.CAPE,
  EquipSlot.NECK,
  EquipSlot.BODY,
  EquipSlot.LEGS,
  EquipSlot.HANDS,
  EquipSlot.FEET,
]);
const ARM_COVERAGE_WEAR_POS = new Set([6]);
const HEAD_STABILIZED_STAND_ANIMATIONS = new Set([1662, 7220, 7518]);
const HEAD_STABILIZATION_NATIVE_OFFSETS: Partial<Record<number, [number, number, number]>> = {
  7220: [0, 0.074, 0.045],
};
const RIG_DEPENDENT_COVERAGE_SLOTS = new Set<number>([
  EquipSlot.LEGS,
  EquipSlot.HANDS,
  EquipSlot.FEET,
]);
const FORCE_RIGID_EQUIPMENT_SLOTS = new Set<number>([
  EquipSlot.HEAD,
  EquipSlot.WEAPON,
  EquipSlot.SHIELD,
]);
const FORCE_RIGID_EQUIPMENT_NAMES = new Set<string>([
  'torva platebody',
  'masori body',
  'masori body (f)',
  'torva full helm',
  'masori mask',
  'masori mask (f)',
  "ava's assembler",
]);
const EQUIPMENT_PRESENTATION_OVERRIDES: Record<string, EquipmentPresentationOverride> = {
  "ava's assembler": {
    offset: [0, 0.016, -0.064],
    rotation: [0.055, 0, 0],
  },
  'infernal cape': {
    mode: 'full',
    nativeOffset: [0, 0, 0],
    nativeRotation: [0, 0, 0],
    nativeMirror: [false, false, false],
  },
  'torva full helm': {
    offset: [0, 0.006, 0.004],
  },
  'masori mask': {
    offset: [0, 0.004, 0.006],
  },
  'masori mask (f)': {
    offset: [0, 0.004, 0.006],
  },
  'torva platebody': {
    offset: [0, 0.003, 0.002],
  },
  'masori body': {
    offset: [0, 0.003, 0.004],
  },
  'masori body (f)': {
    offset: [0, 0.003, 0.004],
  },
  'voidwaker': {
    offset: [0.014, 0.005, 0.011],
    rotation: [0, -0.015, -0.085],
  },
  'dragon scimitar': {
    // Was [-0.001, 0.007, 0.011] — sat just left of the right-hand grip.
    // Pushed +0.013 on x to seat the hilt inside the fist.
    // Confirmed good: dev /3d-test column D/L.
    mode: 'full',
    nativeOffset: [0, 0, 0],
    nativeMirror: [false, false, false],
  },
  'magic shortbow': {
    // Was [0.007, 0.009, 0.026] — same "few pixels left of the hand" gap as
    // dragon scimitar; +0.011 on x lands the grip on the bow-hand.
    // Confirmed good: dev /3d-test column B.
    mode: 'rigid',
    offset: [0, 0, 0],
    rotation: [0, 0, 0],
  },
  'grey hat': {
    mode: 'full',
    offset: [0, 0, 0],
    rotation: [0, 0, 0],
  },
  'fire cape': {
    mode: 'full',
    nativeOffset: [0, 0, 0],
    nativeRotation: [0, 0, 0],
    nativeMirror: [false, false, false],
  },
  'zaryte crossbow': {
    mode: 'full',
    nativeOffset: [0, 0, 0],
    nativeRotation: [0, 0, 0],
    nativeMirror: [false, false, false],
    standAnimation: 4591,
  },
  'armadyl crossbow': {
    mode: 'full',
    nativeOffset: [0, 0, 0],
    nativeRotation: [0, 0, 0],
    nativeMirror: [false, false, false],
    standAnimation: 4591,
  },
  'dragon crossbow': {
    mode: 'full',
    nativeOffset: [0, 0, 0],
    nativeRotation: [0, 0, 0],
    nativeMirror: [false, false, false],
    standAnimation: 4591,
  },
  'dragon hunter crossbow': {
    mode: 'full',
    nativeOffset: [0, 0, 0],
    nativeRotation: [0, 0, 0],
    nativeMirror: [false, false, false],
    standAnimation: 4591,
  },
  "karil's crossbow": {
    // Was [-0.1, -0.16, 0.16] with mirror [F,T,F] — character pose was good
    // but the crossbow body sat forward and below the firing hand and pointed
    // off-axis. Pulled it +0.04 on x toward the hand, lifted +0.06 on y, and
    // pulled -0.06 on z out of the camera so the stock seats in the off-hand
    // and the barrel reads as aimed downrange.
    mode: 'full',
    nativeOffset: [0, 0, 0],
    nativeRotation: [0, 0, 0],
    nativeMirror: [false, false, false],
  },
  'webweaver bow': {
    offset: [0.027, 0.011, 0.033],
    rotation: [0.018, -0.06, -0.16],
  },
  'webweaver bow (u)': {
    offset: [0.027, 0.011, 0.033],
    rotation: [0.018, -0.06, -0.16],
  },
  "tumeken's shadow": {
    mode: 'full',
    nativeOffset: [0, 0, 0],
    nativeRotation: [0, 0, 0],
    nativeMirror: [false, false, false],
    standAnimation: 813,
  },
  "ahrim's staff": {
    mode: 'full',
    nativeOffset: [0, 0, 0],
    nativeRotation: [0, 0, 0],
    nativeMirror: [false, false, false],
    standAnimation: 809,
  },
  'ancestral robe top': {
    mode: 'full',
    nativeOffset: [0, 0, 0],
    nativeRotation: [0, 0, 0],
    nativeMirror: [false, false, false],
  },
  'smoke battlestaff': {
    mode: 'full',
    nativeOffset: [0, 0, 0],
    nativeRotation: [0, 0, 0],
    nativeMirror: [false, false, false],
    standAnimation: 813,
  },
  'sanguinesti staff': {
    offset: [0.023, 0.011, 0.034],
    rotation: [0.012, -0.055, -0.095],
  },
  'eldritch nightmare staff': {
    offset: [0.024, 0.012, 0.038],
    rotation: [0.015, -0.07, -0.12],
  },
  'harmonised nightmare staff': {
    offset: [0.024, 0.012, 0.038],
    rotation: [0.015, -0.07, -0.12],
  },
  'volatile nightmare staff': {
    offset: [0.024, 0.012, 0.038],
    rotation: [0.015, -0.07, -0.12],
  },
  'avernic defender': {
    offset: [-0.013, 0.003, 0.021],
    rotation: [0, 0.11, 0.06],
  },
  'scythe of vitur': {
    mode: 'full',
    nativeOffset: [0, 0, 0],
    nativeRotation: [0, 0, 0],
    nativeMirror: [false, false, false],
    standAnimation: 809,
  },
  'heavy ballista': {
    mode: 'full',
    nativeOffset: [0, 0, 0],
    nativeRotation: [0, 0, 0],
    nativeMirror: [false, false, false],
    standAnimation: 7220,
  },
  'tzhaar-ket-om': {
    mode: 'full',
    nativeOffset: [0, 0, 0],
    nativeRotation: [0, 0, 0],
    nativeMirror: [false, false, false],
    standAnimation: 1662,
  },
  "dinh's bulwark": {
    mode: 'full',
    nativeOffset: [0, 0, 0],
    nativeRotation: [0, 0, 0],
    nativeMirror: [false, false, false],
    standAnimation: 808,
  },
  // 2H stance pose group — angle was correct but the haft was floating in
  // mid-air a few units forward and right of the right hand. Pulled all
  // GREATAXE/GODSWORD nativeOffsets -0.10 on x (further left toward the
  // body) and -0.10 on z (further back into the hand grip).
  "dharok's greataxe": {
    mode: 'full',
    nativeOffset: [0, 0, 0],
    nativeRotation: [0, 0, 0],
    nativeMirror: [false, false, false],
  },
  'armadyl godsword': {
    mode: 'full',
    nativeOffset: [0, 0, 0],
    nativeRotation: [0, 0, 0],
    nativeMirror: [false, false, false],
  },
  'saradomin godsword': {
    mode: 'full',
    nativeOffset: [0, 0, 0],
    nativeRotation: [0, 0, 0],
    nativeMirror: [false, false, false],
  },
  'zamorak godsword': {
    mode: 'full',
    nativeOffset: [0, 0, 0],
    nativeRotation: [0, 0, 0],
    nativeMirror: [false, false, false],
  },
  'bandos godsword': {
    mode: 'full',
    nativeOffset: [0, 0, 0],
    nativeRotation: [0, 0, 0],
    nativeMirror: [false, false, false],
  },
};

const BODY_SLOT_TO_APPEARANCE_SLOTS: Partial<Record<number, number[]>> = {
  [EquipSlot.BODY]: [2, 3],
  [EquipSlot.HANDS]: [4],
  [EquipSlot.LEGS]: [5],
  [EquipSlot.FEET]: [6],
};

export function buildEquipmentAppearanceSignature(
  equipment: Partial<Record<number, EquippedAppearanceItem | null | undefined>>,
  baseAppearance?: BaseCharacterAppearance,
): string {
  const equipmentSignature = EQUIPMENT_SLOTS
    .map((slot) => {
      const item = equipment[slot];
      return item ? `${slot}:${item.id}` : `${slot}:0`;
    })
    .join('|');

  return baseAppearance
    ? `${equipmentSignature}|base:${buildBaseCharacterSignature(baseAppearance)}`
    : equipmentSignature;
}

export function rebuildCharacterBody(
  body: THREE.Group,
  assets: CharacterAppearanceAssets,
  equipment: Partial<Record<number, EquippedAppearanceItem | null | undefined>>,
  baseAppearance?: BaseCharacterAppearance,
  options?: CharacterAssemblyOptions,
): void {
  disposeChildren(body);

  const animationStrategy = resolveCharacterAnimationStrategy(options?.animationStrategy);
  body.userData.animationStrategy = animationStrategy;

  const hiddenBodySlots = collectHiddenAppearanceSlots(
    equipment,
    assets.manifest.items,
    assets.models,
    animationStrategy,
  );
  const kitById = new Map(assets.manifest.kits.map((entry) => [entry.id, entry]));
  const baseColorSwaps = baseAppearance ? resolveBaseCharacterColorSwaps(baseAppearance) : [];
  const baseFaceColorOverrides = baseAppearance ? resolveBaseCharacterFaceColorOverrides(baseAppearance) : {};
  const baseKitMaterial = Object.keys(baseFaceColorOverrides).length > 0
    ? { ...BASE_KIT_MATERIAL, faceColorOverrides: baseFaceColorOverrides }
    : BASE_KIT_MATERIAL;

  if (options?.itemOnly !== true) {
    for (let appearanceSlot = 0; appearanceSlot <= 6; appearanceSlot += 1) {
      const kitId = resolveKitIdForAppearanceSlot(appearanceSlot, assets.manifest, baseAppearance);
      const kit = typeof kitId === 'number' ? kitById.get(kitId) : undefined;
      if (!kit) {
        continue;
      }
      const appearanceSlotGroup = new THREE.Group();
      appearanceSlotGroup.userData.appearanceSlot = appearanceSlot;
      appearanceSlotGroup.visible = !hiddenBodySlots.has(appearanceSlot);
      body.add(appearanceSlotGroup);
      appendModelKeys(
        appearanceSlotGroup,
        assets.models,
        kit.bodyModelKeys,
        new THREE.Vector3(),
        [...baseColorSwaps, ...(kit.recolors ?? [])],
        baseKitMaterial,
        'full',
        MIN_BASE_KIT_RIG_GROUPS,
      );
    }
  }

  for (const equipped of getRenderableEquipment(equipment, assets.manifest.items)) {
    const appearance = assets.manifest.items[String(equipped.id)];
    if (!appearance) {
      continue;
    }
    appendEquippedItem(
      body,
      assets.models,
      appearance,
      appearance.male,
      animationStrategy,
      options?.presentationOverrides,
    );
  }

  initializeRigidAttachments(body);
}

function resolveKitIdForAppearanceSlot(
  appearanceSlot: number,
  manifest: CharacterAppearanceManifest,
  baseAppearance?: BaseCharacterAppearance,
): number | undefined {
  const selectedKitId = baseAppearance?.kitByAppearanceSlot[String(appearanceSlot)];
  const hasSelectedKit = typeof selectedKitId === 'number'
    && manifest.kits.some((entry) => entry.id === selectedKitId && entry.gender !== 'female');

  if (hasSelectedKit) {
    return selectedKitId;
  }

  return manifest.defaults.male.kitByAppearanceSlot[String(appearanceSlot)];
}

export function applyCharacterAnimationSequence(
  body: THREE.Group,
  sequence: CharacterAnimationSequence | null,
  elapsedMs: number,
  options?: { loop?: boolean; holdLastFrame?: boolean; strategy?: CharacterAnimationStrategy },
): void {
  const parts = collectAnimatableParts(body);
  const animationStrategy = resolveCharacterAnimationStrategy(
    options?.strategy ?? (body.userData.animationStrategy as CharacterAnimationStrategy | undefined),
  );
  if (parts.length === 0) {
    updateRigidAttachments(body);
    return;
  }

  if (!sequence || sequence.frames.length === 0) {
    for (const part of parts) {
      resetAnimatablePart(part);
    }
    updateRigidAttachments(body);
    return;
  }

  const frameState = resolveAnimationFrame(sequence, elapsedMs, options?.loop ?? true, options?.holdLastFrame ?? false);
  if (!frameState) {
    for (const part of parts) {
      resetAnimatablePart(part);
    }
    updateRigidAttachments(body);
    return;
  }

  animateParts(
    parts,
    frameState.transforms,
    animationStrategy,
    shouldStabilizeHeadForStand(sequence.id) ? sequence.id : null,
  );

  updateRigidAttachments(body);
}

function appendEquippedItem(
  body: THREE.Group,
  modelCatalog: Record<string, CharacterModelEntry>,
  itemAppearance: CharacterItemAppearance,
  source: CharacterAppearanceSource,
  animationStrategy: CharacterAnimationStrategy,
  presentationOverrides?: Record<string, EquipmentPresentationOverride>,
): void {
  const presentationOverride = resolveEquipmentPresentationOverride(itemAppearance.name, presentationOverrides);
  const translation = source.translation ?? { x: 0, y: source.translationY, z: 0 };
  const offset = new THREE.Vector3(
    translation.x * MODEL_UNIT_SCALE,
    -translation.y * MODEL_UNIT_SCALE,
    -translation.z * MODEL_UNIT_SCALE,
  );
  const animationMode = getEquipmentAnimationMode(itemAppearance, modelCatalog, animationStrategy, presentationOverride);
  const useAnimatedWeaponPose = isWeaponPrimaryEquipment(itemAppearance) && animationMode === 'full';
  const useNativeEquipmentPose = animationMode === 'full'
    && typeof itemAppearance.slot === 'number'
    && NATIVE_POSE_EQUIPMENT_SLOTS.has(itemAppearance.slot);
  const displayOffset = useAnimatedWeaponPose
    ? getNativeWeaponDisplayOffset(itemAppearance, presentationOverride)
    : useNativeEquipmentPose
      ? new THREE.Vector3()
    : getEquipmentDisplayOffset(itemAppearance, presentationOverride);
  const itemGroup = new THREE.Group();
  itemGroup.position.copy(displayOffset);
  itemGroup.rotation.copy(useAnimatedWeaponPose
    ? getNativeWeaponDisplayRotation(presentationOverride)
    : useNativeEquipmentPose
      ? getNativeEquipmentDisplayRotation()
      : getEquipmentDisplayRotation(itemAppearance, presentationOverride));
  itemGroup.userData.equipmentSlot = itemAppearance.slot;
  itemGroup.userData.equipmentName = itemAppearance.name.toLowerCase();
  body.add(itemGroup);
  if (animationMode === 'rigid') {
    itemGroup.userData.rigidAttachment = {
      slot: itemAppearance.slot,
      anchorSlots: getRigidAnchorSlots(itemAppearance),
      aimSlots: getRigidAimSlots(itemAppearance),
      anchorSide: getRigidAnchorSide(itemAppearance),
      aimSide: getRigidAimSide(itemAppearance),
      restPosition: displayOffset.toArray() as [number, number, number],
      restQuaternion: itemGroup.quaternion.toArray() as [number, number, number, number],
    } satisfies RigidAttachmentState;
  }
  appendModelKeys(
    itemGroup,
    modelCatalog,
    source.bodyModelKeys,
    offset,
    getEquipmentColorSwaps(itemAppearance),
    getMaterialStyleForItem(itemAppearance),
    animationMode,
    getMinimumRigGroupCountForAppearance(itemAppearance),
    useAnimatedWeaponPose ? getNativeEquipmentMirror(itemAppearance, presentationOverride) : undefined,
  );
}

function appendModelKeys(
  body: THREE.Group,
  modelCatalog: Record<string, CharacterModelEntry>,
  modelKeys: string[],
  offset = new THREE.Vector3(),
  recolors: CharacterColorSwap[] = [],
  materialStyle: MaterialStyle,
  animationMode: AnimationMode = 'full',
  minimumRigGroupCount = DEFAULT_MIN_EQUIPMENT_RIG_GROUPS,
  nativeMirror?: [boolean, boolean, boolean],
): void {
  const colorRemap = createColorRemap(recolors);
  const hslRemap = createHslRemap(recolors);
  for (const modelKey of modelKeys) {
    const modelMaterialStyle = getModelMaterialStyle(modelKey, materialStyle);
    const mesh = createCharacterMesh(
      modelCatalog[modelKey],
      offset,
      colorRemap,
      hslRemap,
      modelMaterialStyle,
      animationMode,
      minimumRigGroupCount,
      nativeMirror,
    );
    if (mesh) {
      mesh.userData.modelKey = modelKey;
      if (body.userData.appearanceSlot != null) {
        mesh.userData.appearanceSlot = body.userData.appearanceSlot;
      }
      if (body.userData.equipmentSlot != null) {
        mesh.userData.equipmentSlot = body.userData.equipmentSlot;
        mesh.renderOrder = getEquipmentRenderOrder(body.userData.equipmentSlot);
      }
      if (body.userData.equipmentName != null) {
        mesh.userData.equipmentName = body.userData.equipmentName;
      }
      const animationData = mesh.userData.animationData as CharacterMeshAnimationData | undefined;
      if (animationData) {
        animationData.pivotSource = typeof body.userData.appearanceSlot === 'number';
        if (typeof body.userData.appearanceSlot === 'number') {
          animationData.appearanceSlot = body.userData.appearanceSlot;
        }
        if (typeof body.userData.equipmentSlot === 'number') {
          animationData.equipmentSlot = body.userData.equipmentSlot;
        }
        if (typeof body.userData.equipmentName === 'string') {
          animationData.equipmentName = body.userData.equipmentName;
        }
      }
      body.add(mesh);
    }
  }
}

function getModelMaterialStyle(modelKey: string, materialStyle: MaterialStyle): MaterialStyle {
  if (!isCustomSkillcapeIconModel(modelKey)) {
    return materialStyle;
  }

  return {
    ...materialStyle,
    radialOutset: 0,
    capeEmblemOverlayFaces: false,
    nudgeFlatOverlayFaces: false,
    nudgePriorityFaces: false,
    skipLargeCapeArtifactFaces: false,
    smoothNormals: false,
    doubleSided: true,
    polygonOffset: true,
    polygonOffsetFactor: -6,
    polygonOffsetUnits: -6,
    depthWrite: false,
    textureDepthWrite: false,
  };
}

function isCustomSkillcapeIconModel(modelKey: string): boolean {
  return /^soloscape-render:.*-cape-icon(?:-.+)?$/.test(modelKey);
}

function createCharacterMesh(
  model: CharacterModelEntry | undefined,
  offset: THREE.Vector3,
  colorRemap: Map<string, [number, number, number]>,
  hslRemap: Map<number, number>,
  materialStyle: MaterialStyle,
  animationMode: AnimationMode,
  minimumRigGroupCount: number,
  nativeMirror?: [boolean, boolean, boolean],
): THREE.Mesh | null {
  if (!model) {
    return null;
  }

  const sourceRenderPositions = new Float32Array(model.vertices.length);
  const baseNativePositions = new Float32Array(model.vertices.length);
  for (let index = 0; index < model.vertices.length; index += 3) {
    const renderX = (model.vertices[index] ?? 0) + offset.x;
    const renderY = (model.vertices[index + 1] ?? 0) + offset.y;
    const renderZ = -((model.vertices[index + 2] ?? 0) - offset.z);

    sourceRenderPositions[index] = renderX;
    sourceRenderPositions[index + 1] = renderY;
    sourceRenderPositions[index + 2] = renderZ;

    // Keep animation data in native OSRS model-space, then convert back when writing the mesh.
    baseNativePositions[index] = renderX;
    baseNativePositions[index + 1] = -renderY;
    baseNativePositions[index + 2] = -renderZ;
  }
  applyNativeMirror(sourceRenderPositions, baseNativePositions, nativeMirror);
  const attachesAnimationData = animationMode === 'full' && modelHasUsableAnimationRig(model, minimumRigGroupCount);
  const shouldApplyStaticRadialOutset = !attachesAnimationData && (materialStyle.radialOutset ?? 0) > 0;
  const resolveStaticRenderPosition = (x: number, y: number, z: number): [number, number, number] => (
    shouldApplyStaticRadialOutset
      ? applyRadialOutsetToRenderPosition(
          x,
          y,
          z,
          materialStyle.radialOutset,
          materialStyle.radialOutsetTopY,
          materialStyle.radialOutsetHeight,
          materialStyle.radialOutsetMinFactor,
        )
      : [x, y, z]
  );

  const positions: number[] = [];
  const colors: number[] = [];
  const uvs: number[] = [];
  const materialGroups: Array<{ start: number; count: number; materialIndex: number }> = [];
  const expandedVertexSources: number[] = [];
  const renderPositionNudges: number[] = [];
  const depthFillPositions: number[] = [];
  const depthFillColors: number[] = [];
  const depthFillUvs: number[] = [];
  const depthFillGroups: Array<{ start: number; count: number; materialIndex: number }> = [];
  const depthFillExpandedVertexSources: number[] = [];
  const depthFillRenderPositionNudges: number[] = [];
  const faceColors = model.faceColors;
  const faceTextures = model.faceTextures;
  const faceRenderPriorities = model.faceRenderPriorities;
  const faceOrder = getModelFaceOrder(model);
  const texturePaths = materialStyle.texturePaths ?? {};
  const usesInfernalProceduralTexture = materialStyle.proceduralTexture === 'infernal-cape';
  const hasUnboundSourceTextures = Boolean(faceTextures?.some((textureId) => (
    typeof textureId === 'number' && textureId >= 0 && texturePaths[textureId] == null
  )));
  const usesDepthFillMaterial = materialStyle.depthFillLowerCape === true && !hasUnboundSourceTextures;
  const usesCapeEmblemOverlayMaterial = materialStyle.capeEmblemOverlayFaces === true;
  const usesTextureMaterial = Boolean(faceTextures?.some((textureId) => (
    typeof textureId === 'number' && texturePaths[textureId] != null
  )) || usesInfernalProceduralTexture);
  const usesAlphaTexture = Object.values(texturePaths).some((path) => path.includes('/sprites/skill/'));
  const usesGroupedMaterials = usesTextureMaterial || usesDepthFillMaterial || usesCapeEmblemOverlayMaterial;
  const capeEmblemMaterialIndex = 1 + (usesTextureMaterial ? 1 : 0) + (usesDepthFillMaterial ? 1 : 0);
  const capeEmblemOverlayPositions: number[] = [];
  const capeEmblemOverlayColors: number[] = [];
  const capeEmblemOverlayUvs: number[] = [];
  const capeEmblemOverlayGroups: Array<{ start: number; count: number; materialIndex: number }> = [];
  const capeEmblemOverlayExpandedVertexSources: number[] = [];
  const capeEmblemOverlayRenderPositionNudges: number[] = [];
  for (const faceIndex of faceOrder) {
    const index = faceIndex * 3;
    if ((model.alphas?.[faceIndex] ?? 0) >= 254) {
      continue;
    }
    const faceTextureId = faceTextures?.[faceIndex] ?? -1;
    const faceColor = faceColors?.[faceIndex];
    const usesProceduralTextureFace = usesInfernalProceduralTexture
      && isInfernalCapeTexturedFace(sourceRenderPositions, model.indices, index, faceColor);
    const materialIndex = usesProceduralTextureFace
      ? 1
      : typeof faceTextureId === 'number' && texturePaths[faceTextureId] != null ? 1 : 0;
    if (
      materialIndex === 0
      && materialStyle.skipLargeCapeArtifactFaces
      && faceColor === 0
      && isLargeCapeArtifactFace(sourceRenderPositions, model.indices, index)
    ) {
      continue;
    }
    if (
      materialIndex === 0
      && typeof faceColor === 'number'
      && materialStyle.skipUntexturedFaceColors?.includes(faceColor)
    ) {
      continue;
    }
    const groupStart = positions.length / 3;
    let r: number;
    let g: number;
    let b: number;
    let faceNudge: [number, number, number] | null = null;
    let cornerColors: Array<[number, number, number]> | null = null;
    // Prefer per-face HSL16 lookup when available (post-fix RSPK extractions
    // include `faceColors`). The remap is keyed by the source HSL16 key, so a
    // face whose key matches a recolor entry gets remapped before the HSL→RGB
    // conversion. This avoids the rounding-mismatch precision loss that the
    // old RGB-keyed lookup suffered from.
    if (faceColors && typeof faceColors[faceIndex] === 'number') {
      const hsl = faceColors[faceIndex];
      const explicitColor = materialStyle.faceColorOverrides?.[hsl];
      const remappedHsl = explicitColor ? hsl : (hslRemap.get(hsl) ?? hsl);
      // HSL16=0 in OSRS player heads marks the eye-pupil triangles. Render
      // them as a small dark colour rather than pure black so they show as
      // actual eyes (small dots) instead of disappearing entirely. Without
      // this, the head looks like it has no facial features.
      if (explicitColor) {
        [r, g, b] = explicitColor;
        if (materialStyle.nudgeAllFaces || materialStyle.nudgeFaceColors?.includes(hsl)) {
          faceNudge = computeFaceNudge(
            sourceRenderPositions,
            model.indices,
            index,
            materialStyle.faceNudgeAmount ?? 0.004,
          );
        }
      } else if (remappedHsl === 0) {
        r = 0.06; g = 0.05; b = 0.04;
        faceNudge = computeFaceNudge(sourceRenderPositions, model.indices, index, 0.0035);
      } else {
        const rgb = hslToRgbNormalized(remappedHsl);
        r = rgb[0]; g = rgb[1]; b = rgb[2];
        if (materialStyle.nudgeAllFaces || materialStyle.nudgeFaceColors?.includes(hsl)) {
          faceNudge = computeFaceNudge(
            sourceRenderPositions,
            model.indices,
            index,
            materialStyle.faceNudgeAmount ?? 0.004,
          );
        }
      }
    } else {
      const colorIndex = index;
      const sourceColor: [number, number, number] = [
        model.colors[colorIndex] ?? 0.65,
        model.colors[colorIndex + 1] ?? (model.colors[colorIndex] ?? 0.65),
        model.colors[colorIndex + 2] ?? (model.colors[colorIndex + 1] ?? model.colors[colorIndex] ?? 0.65),
      ];
      [r, g, b] = colorRemap.get(colorKey(sourceColor[0], sourceColor[1], sourceColor[2])) ?? sourceColor;
    }
    if (materialStyle.infernalLavaPattern) {
      const patterned = getInfernalCapeCornerColors(sourceRenderPositions, model.indices, index, faceColor);
      if (patterned) {
        cornerColors = patterned;
        [r, g, b] = patterned[0] ?? [r, g, b];
      }
    }
    if (!hasUnboundSourceTextures && materialStyle.nudgeFlatOverlayFaces && isLikelyFlatCapeOverlayFace(sourceRenderPositions, model.indices, index)) {
      const overlayNudge = computeFaceNudge(
        sourceRenderPositions,
        model.indices,
        index,
        materialStyle.flatOverlayFaceNudgeAmount ?? 0.012,
      );
      if (overlayNudge) {
        faceNudge = chooseLargerFaceNudge(faceNudge, overlayNudge);
      }
    }
    const facePriority = faceRenderPriorities?.[faceIndex] ?? 0;
    if (!hasUnboundSourceTextures && materialStyle.nudgePriorityFaces && facePriority > 0) {
      const priorityNudge = computeFaceNudge(
        sourceRenderPositions,
        model.indices,
        index,
        getFacePriorityNudgeAmount(facePriority, materialStyle),
      );
      if (priorityNudge) {
        faceNudge = chooseLargerFaceNudge(faceNudge, priorityNudge);
      }
    }
    const isCapeEmblemOverlayFace = usesCapeEmblemOverlayMaterial
      && materialIndex === 0
      && typeof faceColor === 'number'
      && isLikelyCapeEmblemOverlayFace(sourceRenderPositions, model.indices, index, faceColor, facePriority);
    const capeEmblemOverlayNudge = isCapeEmblemOverlayFace
      ? computeFaceNudge(
          sourceRenderPositions,
          model.indices,
          index,
          materialStyle.capeEmblemOverlayNudgeAmount ?? 0.014,
        )
      : null;

    for (let corner = 0; corner < 3; corner += 1) {
      const sourceVertexIndex = model.indices[index + corner];
      if (typeof sourceVertexIndex !== 'number') {
        continue;
      }
      const vertexIndex = sourceVertexIndex * 3;
      const renderPosition = resolveStaticRenderPosition(
        (sourceRenderPositions[vertexIndex] ?? 0) + (faceNudge?.[0] ?? 0),
        (sourceRenderPositions[vertexIndex + 1] ?? 0) + (faceNudge?.[1] ?? 0),
        (sourceRenderPositions[vertexIndex + 2] ?? 0) + (faceNudge?.[2] ?? 0),
      );
      positions.push(...renderPosition);
      colors.push(...(cornerColors?.[corner] ?? [r, g, b]));
      if (materialIndex === 1) {
        if (usesProceduralTextureFace) {
          uvs.push(
            Math.max(0, Math.min(1, ((sourceRenderPositions[vertexIndex] ?? 0) + 0.29) / 0.58)),
            Math.max(0, Math.min(1, (1.28 - (sourceRenderPositions[vertexIndex + 1] ?? 0)) / 1.08)),
          );
        } else {
          uvs.push(
            model.faceTextureUCoordinates?.[faceIndex]?.[corner] ?? (corner === 1 ? 1 : 0),
            model.faceTextureVCoordinates?.[faceIndex]?.[corner] ?? (corner === 2 ? 1 : 0),
          );
        }
      } else {
        uvs.push(0, 0);
      }
      expandedVertexSources.push(sourceVertexIndex);
      renderPositionNudges.push(faceNudge?.[0] ?? 0, faceNudge?.[1] ?? 0, faceNudge?.[2] ?? 0);
    }
    materialGroups.push({ start: groupStart, count: 3, materialIndex });
    if (
      usesDepthFillMaterial
      && typeof faceColor === 'number'
      && isCapeDepthFillFace(sourceRenderPositions, model.indices, index, faceColor, facePriority)
    ) {
      const fillMaterialIndex = usesTextureMaterial ? 2 : 1;
      const fillGroupStart = depthFillPositions.length / 3;
      for (let corner = 0; corner < 3; corner += 1) {
        const sourceVertexIndex = model.indices[index + corner];
        if (typeof sourceVertexIndex !== 'number') {
          continue;
        }
        const vertexIndex = sourceVertexIndex * 3;
        const renderPosition = resolveStaticRenderPosition(
          (sourceRenderPositions[vertexIndex] ?? 0) + (faceNudge?.[0] ?? 0),
          (sourceRenderPositions[vertexIndex + 1] ?? 0) + (faceNudge?.[1] ?? 0),
          (sourceRenderPositions[vertexIndex + 2] ?? 0) + (faceNudge?.[2] ?? 0),
        );
        depthFillPositions.push(...renderPosition);
        depthFillColors.push(...(cornerColors?.[corner] ?? [r, g, b]));
        depthFillUvs.push(0, 0);
        depthFillExpandedVertexSources.push(sourceVertexIndex);
        depthFillRenderPositionNudges.push(faceNudge?.[0] ?? 0, faceNudge?.[1] ?? 0, faceNudge?.[2] ?? 0);
      }
      depthFillGroups.push({ start: fillGroupStart, count: 3, materialIndex: fillMaterialIndex });
    }
    if (isCapeEmblemOverlayFace) {
      const overlayGroupStart = capeEmblemOverlayPositions.length / 3;
      const overlayNudge = capeEmblemOverlayNudge
        ? chooseLargerFaceNudge(faceNudge, capeEmblemOverlayNudge)
        : faceNudge;
      const overlayColor = enhanceCapeEmblemOverlayColor(cornerColors?.[0] ?? [r, g, b], faceColor, facePriority);
      for (let corner = 0; corner < 3; corner += 1) {
        const sourceVertexIndex = model.indices[index + corner];
        if (typeof sourceVertexIndex !== 'number') {
          continue;
        }
        const vertexIndex = sourceVertexIndex * 3;
        const renderPosition = resolveStaticRenderPosition(
          (sourceRenderPositions[vertexIndex] ?? 0) + (overlayNudge?.[0] ?? 0),
          (sourceRenderPositions[vertexIndex + 1] ?? 0) + (overlayNudge?.[1] ?? 0),
          (sourceRenderPositions[vertexIndex + 2] ?? 0) + (overlayNudge?.[2] ?? 0),
        );
        capeEmblemOverlayPositions.push(...renderPosition);
        capeEmblemOverlayColors.push(...(cornerColors ? enhanceCapeEmblemOverlayColor(cornerColors[corner] ?? overlayColor, faceColor, facePriority) : overlayColor));
        capeEmblemOverlayUvs.push(0, 0);
        capeEmblemOverlayExpandedVertexSources.push(sourceVertexIndex);
        capeEmblemOverlayRenderPositionNudges.push(
          overlayNudge?.[0] ?? 0,
          overlayNudge?.[1] ?? 0,
          overlayNudge?.[2] ?? 0,
        );
      }
      capeEmblemOverlayGroups.push({ start: overlayGroupStart, count: 3, materialIndex: capeEmblemMaterialIndex });
    }
  }

  const depthFillVertexCount = depthFillPositions.length / 3;
  const capeEmblemOverlayVertexCount = capeEmblemOverlayPositions.length / 3;
  const finalPositions = [
    ...positions,
    ...(depthFillVertexCount > 0 ? depthFillPositions : []),
    ...(capeEmblemOverlayVertexCount > 0 ? capeEmblemOverlayPositions : []),
  ];
  const finalColors = [
    ...colors,
    ...(depthFillVertexCount > 0 ? depthFillColors : []),
    ...(capeEmblemOverlayVertexCount > 0 ? capeEmblemOverlayColors : []),
  ];
  const finalUvs = [
    ...uvs,
    ...(depthFillVertexCount > 0 ? depthFillUvs : []),
    ...(capeEmblemOverlayVertexCount > 0 ? capeEmblemOverlayUvs : []),
  ];
  const finalExpandedVertexSources = [
    ...expandedVertexSources,
    ...(depthFillVertexCount > 0 ? depthFillExpandedVertexSources : []),
    ...(capeEmblemOverlayVertexCount > 0 ? capeEmblemOverlayExpandedVertexSources : []),
  ];
  const finalRenderPositionNudges = [
    ...renderPositionNudges,
    ...(depthFillVertexCount > 0 ? depthFillRenderPositionNudges : []),
    ...(capeEmblemOverlayVertexCount > 0 ? capeEmblemOverlayRenderPositionNudges : []),
  ];
  const finalMaterialGroups: Array<{ start: number; count: number; materialIndex: number }> = [...materialGroups];
  if (depthFillVertexCount > 0) {
    const depthFillOffset = positions.length / 3;
    finalMaterialGroups.push(
      ...depthFillGroups.map((group) => ({
        ...group,
        start: group.start + depthFillOffset,
      })),
    );
  }
  if (capeEmblemOverlayVertexCount > 0) {
    const capeEmblemOverlayOffset = (positions.length / 3) + depthFillVertexCount;
    finalMaterialGroups.push(
      ...capeEmblemOverlayGroups.map((group) => ({
        ...group,
        start: group.start + capeEmblemOverlayOffset,
      })),
    );
  }

  const geometry = new THREE.BufferGeometry();
  const positionAttribute = new THREE.Float32BufferAttribute(finalPositions, 3);
  geometry.setAttribute('position', positionAttribute);
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(finalColors, 3));
  if (usesTextureMaterial) {
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(finalUvs, 2));
  }
  if (usesGroupedMaterials) {
    for (const group of finalMaterialGroups) {
      geometry.addGroup(group.start, group.count, group.materialIndex);
    }
  }
  if (materialStyle.smoothNormals) {
    applySmoothExpandedNormals(geometry, finalExpandedVertexSources);
  } else {
    geometry.computeVertexNormals();
  }

  const baseMaterial = new THREE.MeshStandardMaterial({
    vertexColors: true,
    flatShading: !materialStyle.smoothNormals,
    roughness: materialStyle.roughness,
    metalness: materialStyle.metalness,
    emissive: '#130f0a',
    emissiveIntensity: materialStyle.emissiveIntensity,
    side: materialStyle.doubleSided === true ? THREE.DoubleSide : THREE.FrontSide,
    polygonOffset: materialStyle.polygonOffset ?? false,
    polygonOffsetFactor: materialStyle.polygonOffsetFactor ?? 0,
    polygonOffsetUnits: materialStyle.polygonOffsetUnits ?? 0,
    depthTest: materialStyle.depthTest ?? true,
    depthWrite: materialStyle.depthWrite ?? true,
  });
  const textureMaterial = usesTextureMaterial
    ? new THREE.MeshStandardMaterial({
        map: usesInfernalProceduralTexture
          ? getInfernalCapeProceduralTexture()
          : getEquipmentTexture(Object.values(texturePaths)[0]!),
        flatShading: !materialStyle.smoothNormals,
        roughness: materialStyle.roughness,
        metalness: materialStyle.metalness,
        emissive: '#130f0a',
        emissiveIntensity: Math.max(materialStyle.emissiveIntensity, 0.08),
        side: materialStyle.doubleSided === true ? THREE.DoubleSide : THREE.FrontSide,
        transparent: false,
        alphaTest: usesAlphaTexture ? 0.08 : 0,
        depthTest: materialStyle.depthTest ?? true,
        depthWrite: materialStyle.textureDepthWrite ?? (materialStyle.depthWrite ?? true),
      })
    : null;
  const depthFillMaterial = usesDepthFillMaterial
    ? new THREE.MeshStandardMaterial({
        vertexColors: true,
        flatShading: !materialStyle.smoothNormals,
        roughness: materialStyle.roughness,
        metalness: materialStyle.metalness,
        emissive: '#130f0a',
        emissiveIntensity: materialStyle.emissiveIntensity,
        side: THREE.DoubleSide,
        depthTest: false,
        depthWrite: false,
      })
    : null;
  const capeEmblemOverlayMaterial = usesCapeEmblemOverlayMaterial
    ? new THREE.MeshStandardMaterial({
        vertexColors: true,
        flatShading: !materialStyle.smoothNormals,
        roughness: Math.min(materialStyle.roughness, 0.86),
        metalness: materialStyle.metalness,
        emissive: '#17110a',
        emissiveIntensity: Math.max(materialStyle.emissiveIntensity, 0.1),
        side: THREE.DoubleSide,
        polygonOffset: true,
        polygonOffsetFactor: -1,
        polygonOffsetUnits: -1,
        depthTest: true,
        depthWrite: false,
      })
    : null;

  const materials = [
    baseMaterial,
    ...(textureMaterial ? [textureMaterial] : []),
    ...(depthFillMaterial ? [depthFillMaterial] : []),
    ...(capeEmblemOverlayMaterial ? [capeEmblemOverlayMaterial] : []),
  ];
  const mesh = new THREE.Mesh(
    geometry,
    usesGroupedMaterials ? materials : baseMaterial,
  );
  if (attachesAnimationData) {
    mesh.userData.animationData = {
      baseNativePositions,
      workingNativePositions: new Float32Array(baseNativePositions),
      expandedVertexSources: Uint32Array.from(finalExpandedVertexSources),
      renderPositionNudges: finalRenderPositionNudges.some((value) => value !== 0)
        ? Float32Array.from(finalRenderPositionNudges)
        : undefined,
      smoothNormals: materialStyle.smoothNormals,
      radialOutset: materialStyle.radialOutset,
      radialOutsetTopY: materialStyle.radialOutsetTopY,
      radialOutsetHeight: materialStyle.radialOutsetHeight,
      radialOutsetMinFactor: materialStyle.radialOutsetMinFactor,
      positionAttribute,
      geometry,
      vertexGroups: model.vertexGroups ?? [],
      pivotSource: false,
    } as CharacterMeshAnimationData;
  }

  return mesh;
}

function getEquipmentTexture(path: string): THREE.Texture {
  let texture = EQUIPMENT_TEXTURE_CACHE.get(path);
  if (!texture) {
    const isPixelIcon = path.includes('/sprites/skill/');
    texture = new THREE.TextureLoader().load(path);
    texture.wrapS = isPixelIcon ? THREE.ClampToEdgeWrapping : THREE.RepeatWrapping;
    texture.wrapT = isPixelIcon ? THREE.ClampToEdgeWrapping : THREE.RepeatWrapping;
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.minFilter = isPixelIcon ? THREE.NearestFilter : THREE.LinearMipMapLinearFilter;
    texture.magFilter = isPixelIcon ? THREE.NearestFilter : THREE.LinearFilter;
    texture.needsUpdate = true;
    EQUIPMENT_TEXTURE_CACHE.set(path, texture);
  }
  return texture;
}

function getInfernalCapeProceduralTexture(): THREE.Texture {
  const cacheKey = 'procedural:infernal-cape';
  let texture = EQUIPMENT_TEXTURE_CACHE.get(cacheKey);
  if (texture) {
    return texture;
  }

  const width = 192;
  const height = 384;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) {
    texture = new THREE.Texture();
    EQUIPMENT_TEXTURE_CACHE.set(cacheKey, texture);
    return texture;
  }

  const imageData = context.createImageData(width, height);
  const sites: Array<{ x: number; y: number }> = [];
  let seed = 0x5a17;
  const random = (): number => {
    seed = ((seed * 1664525) + 1013904223) >>> 0;
    return seed / 0x100000000;
  };
  for (let index = 0; index < 42; index += 1) {
    sites.push({
      x: random(),
      y: random(),
    });
  }

  const writePixel = (offset: number, color: [number, number, number]): void => {
    imageData.data[offset] = Math.max(0, Math.min(255, Math.round(color[0] * 255)));
    imageData.data[offset + 1] = Math.max(0, Math.min(255, Math.round(color[1] * 255)));
    imageData.data[offset + 2] = Math.max(0, Math.min(255, Math.round(color[2] * 255)));
    imageData.data[offset + 3] = 255;
  };

  for (let py = 0; py < height; py += 1) {
    const v = py / (height - 1);
    for (let px = 0; px < width; px += 1) {
      const u = px / (width - 1);
      let nearest = Number.POSITIVE_INFINITY;
      let nextNearest = Number.POSITIVE_INFINITY;
      for (const site of sites) {
        const dx = (u - site.x) * 1.15;
        const dy = (v - site.y) * 0.78;
        const distance = (dx * dx) + (dy * dy);
        if (distance < nearest) {
          nextNearest = nearest;
          nearest = distance;
        } else if (distance < nextNearest) {
          nextNearest = distance;
        }
      }

      const edge = Math.sqrt(nextNearest) - Math.sqrt(nearest);
      const crack = 1 - smoothstep(0.012, 0.052, edge);
      const glow = smoothstep(0.1, 0.72, crack);
      const core = smoothstep(0.62, 0.96, crack);
      const sideShadow = smoothstep(0.38, 0.5, Math.abs(u - 0.5));
      const topBottomShadow = Math.max(1 - smoothstep(0, 0.08, v), smoothstep(0.92, 1, v));
      const rockNoise = (Math.sin((u * 47.2) + (v * 19.1)) + Math.sin((u * 13.9) - (v * 31.3))) * 0.008;

      let color = mixColor([0.025 + rockNoise, 0.024 + rockNoise, 0.023 + rockNoise], [0.34, 0.035, 0.018], glow * 0.9);
      color = mixColor(color, [0.94, 0.5, 0.035], core * 0.92);
      color = mixColor(color, [1, 0.78, 0.07], Math.max(0, core - 0.52) * 0.65);
      color = mixColor(color, [0.01, 0.01, 0.01], (sideShadow * 0.28) + (topBottomShadow * 0.18));
      writePixel(((py * width) + px) * 4, color);
    }
  }

  context.putImageData(imageData, 0, 0);
  texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearMipMapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.needsUpdate = true;
  EQUIPMENT_TEXTURE_CACHE.set(cacheKey, texture);
  return texture;
}

function getEquipmentRenderOrder(slot: number): number {
  switch (slot) {
    case EquipSlot.CAPE:
      return 12;
    case EquipSlot.BODY:
    case EquipSlot.LEGS:
    case EquipSlot.FEET:
      return 0;
    case EquipSlot.NECK:
      return 20;
    case EquipSlot.HEAD:
      return 30;
    case EquipSlot.WEAPON:
    case EquipSlot.SHIELD:
    case EquipSlot.HANDS:
      return 40;
    default:
      return 10;
  }
}

function applyNativeMirror(
  sourceRenderPositions: Float32Array,
  baseNativePositions: Float32Array,
  mirror?: [boolean, boolean, boolean],
): void {
  if (!mirror?.some(Boolean)) {
    return;
  }

  const pivot: [number, number, number] = [0, 0, 0];
  for (let axis = 0; axis < 3; axis += 1) {
    if (!mirror[axis]) {
      continue;
    }

    let min = Infinity;
    let max = -Infinity;
    for (let index = axis; index < baseNativePositions.length; index += 3) {
      const value = baseNativePositions[index] ?? 0;
      min = Math.min(min, value);
      max = Math.max(max, value);
    }
    pivot[axis] = Number.isFinite(min) && Number.isFinite(max) ? (min + max) / 2 : 0;
  }

  for (let index = 0; index < baseNativePositions.length; index += 3) {
    if (mirror[0]) {
      baseNativePositions[index] = 2 * pivot[0] - (baseNativePositions[index] ?? 0);
    }
    if (mirror[1]) {
      baseNativePositions[index + 1] = 2 * pivot[1] - (baseNativePositions[index + 1] ?? 0);
    }
    if (mirror[2]) {
      baseNativePositions[index + 2] = 2 * pivot[2] - (baseNativePositions[index + 2] ?? 0);
    }

    sourceRenderPositions[index] = baseNativePositions[index] ?? 0;
    sourceRenderPositions[index + 1] = -(baseNativePositions[index + 1] ?? 0);
    sourceRenderPositions[index + 2] = -(baseNativePositions[index + 2] ?? 0);
  }
}

function getModelFaceOrder(model: CharacterModelEntry): number[] {
  const faceCount = Math.floor(model.indices.length / 3);
  const order = Array.from({ length: faceCount }, (_, index) => index);
  const priorities = model.faceRenderPriorities;
  if (!priorities || priorities.length < faceCount) {
    return order;
  }

  return order.sort((a, b) => {
    const priorityDelta = (priorities[a] ?? 0) - (priorities[b] ?? 0);
    return priorityDelta !== 0 ? priorityDelta : a - b;
  });
}

function getFacePriorityNudgeAmount(priority: number, style: MaterialStyle): number {
  const base = style.priorityFaceNudgeAmount ?? 0.0035;
  return base * Math.max(1, priority - 3);
}

function chooseLargerFaceNudge(
  current: [number, number, number] | null,
  next: [number, number, number],
): [number, number, number] {
  if (!current) {
    return next;
  }

  const currentLength = Math.hypot(current[0], current[1], current[2]);
  const nextLength = Math.hypot(next[0], next[1], next[2]);
  return nextLength > currentLength ? next : current;
}

function isLikelyFlatCapeOverlayFace(
  positions: Float32Array,
  indices: number[],
  faceIndexOffset: number,
): boolean {
  const vertices = [
    (indices[faceIndexOffset] ?? -1) * 3,
    (indices[faceIndexOffset + 1] ?? -1) * 3,
    (indices[faceIndexOffset + 2] ?? -1) * 3,
  ];
  if (vertices.some((vertexIndex) => vertexIndex < 0)) {
    return false;
  }

  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;
  let centroidX = 0;
  let centroidY = 0;

  for (const vertexIndex of vertices) {
    const x = positions[vertexIndex] ?? 0;
    const y = positions[vertexIndex + 1] ?? 0;
    const z = positions[vertexIndex + 2] ?? 0;
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
    minZ = Math.min(minZ, z);
    maxZ = Math.max(maxZ, z);
    centroidX += x;
    centroidY += y;
  }

  centroidX /= 3;
  centroidY /= 3;

  return centroidY >= 0.58
    && centroidY <= 1.13
    && Math.abs(centroidX) <= 0.19
    && (maxX - minX) <= 0.42
    && (maxY - minY) <= 0.42
    && (maxZ - minZ) <= 0.035;
}

function isLikelyCapeEmblemOverlayFace(
  positions: Float32Array,
  indices: number[],
  faceIndexOffset: number,
  faceColor: number,
  facePriority: number,
): boolean {
  if (facePriority < 6 || SKILLCAPE_SHARED_BODY_HSL.has(faceColor)) {
    return false;
  }

  const bounds = getFaceBounds(positions, indices, faceIndexOffset);
  if (!bounds) {
    return false;
  }

  const width = bounds.maxX - bounds.minX;
  const height = bounds.maxY - bounds.minY;
  const depth = bounds.maxZ - bounds.minZ;
  const centroidX = (bounds.minX + bounds.maxX) / 2;
  const centroidZ = (bounds.minZ + bounds.maxZ) / 2;
  return bounds.centroidY >= 0.56
    && bounds.centroidY <= 1.14
    && Math.abs(centroidX) <= 0.22
    && bounds.minX >= -0.18
    && bounds.maxX <= 0.18
    && width <= 0.34
    && height <= 0.36
    && depth <= 0.07
    && bounds.area <= 0.009
    && centroidZ <= -0.075;
}

function enhanceCapeEmblemOverlayColor(
  color: [number, number, number],
  faceColor?: number,
  facePriority = 0,
): [number, number, number] {
  if (facePriority < 6 || faceColor === 0) {
    return color;
  }

  const luma = color[0] * 0.2126 + color[1] * 0.7152 + color[2] * 0.0722;
  const mixAmount = luma < 0.18 ? 0.36 : luma < 0.32 ? 0.26 : luma < 0.45 ? 0.14 : 0;
  if (mixAmount <= 0) {
    return color;
  }

  return [
    color[0] + (1 - color[0]) * mixAmount,
    color[1] + (1 - color[1]) * mixAmount,
    color[2] + (1 - color[2]) * mixAmount,
  ];
}

function getFaceBounds(
  positions: Float32Array,
  indices: number[],
  faceIndexOffset: number,
): {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  minZ: number;
  maxZ: number;
  centroidY: number;
  area: number;
} | null {
  const vertices = [
    (indices[faceIndexOffset] ?? -1) * 3,
    (indices[faceIndexOffset + 1] ?? -1) * 3,
    (indices[faceIndexOffset + 2] ?? -1) * 3,
  ];
  if (vertices.some((vertexIndex) => vertexIndex < 0)) {
    return null;
  }

  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;
  let centroidY = 0;

  for (const vertexIndex of vertices) {
    const x = positions[vertexIndex] ?? 0;
    const y = positions[vertexIndex + 1] ?? 0;
    const z = positions[vertexIndex + 2] ?? 0;
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
    minZ = Math.min(minZ, z);
    maxZ = Math.max(maxZ, z);
    centroidY += y;
  }

  const ia = vertices[0] ?? 0;
  const ib = vertices[1] ?? 0;
  const ic = vertices[2] ?? 0;
  const abx = (positions[ib] ?? 0) - (positions[ia] ?? 0);
  const aby = (positions[ib + 1] ?? 0) - (positions[ia + 1] ?? 0);
  const abz = (positions[ib + 2] ?? 0) - (positions[ia + 2] ?? 0);
  const acx = (positions[ic] ?? 0) - (positions[ia] ?? 0);
  const acy = (positions[ic + 1] ?? 0) - (positions[ia + 1] ?? 0);
  const acz = (positions[ic + 2] ?? 0) - (positions[ia + 2] ?? 0);
  const crossX = (aby * acz) - (abz * acy);
  const crossY = (abz * acx) - (abx * acz);
  const crossZ = (abx * acy) - (aby * acx);

  return {
    minX,
    maxX,
    minY,
    maxY,
    minZ,
    maxZ,
    centroidY: centroidY / 3,
    area: Math.hypot(crossX, crossY, crossZ) / 2,
  };
}

function isLargeCapeArtifactFace(
  positions: Float32Array,
  indices: number[],
  faceIndexOffset: number,
): boolean {
  const bounds = getFaceBounds(positions, indices, faceIndexOffset);
  if (!bounds) {
    return false;
  }

  const width = bounds.maxX - bounds.minX;
  const height = bounds.maxY - bounds.minY;
  return bounds.area >= 0.018
    || (width >= 0.35 && height >= 0.24);
}

function isCapeDepthFillFace(
  positions: Float32Array,
  indices: number[],
  faceIndexOffset: number,
  faceColor: number,
  facePriority: number,
): boolean {
  if (facePriority !== 5 || faceColor === 0 || SKILLCAPE_SHARED_BODY_HSL.has(faceColor)) {
    return false;
  }

  const bounds = getFaceBounds(positions, indices, faceIndexOffset);
  if (!bounds) {
    return false;
  }

  const width = bounds.maxX - bounds.minX;
  const height = bounds.maxY - bounds.minY;
  return bounds.centroidY >= 0.18
    && bounds.centroidY <= 1.2
    && bounds.minX >= -0.24
    && bounds.maxX <= 0.24
    && width <= 0.22
    && height <= 0.52
    && bounds.maxZ <= -0.1
    && bounds.area >= 0.00008;
}

function smoothstep(edge0: number, edge1: number, value: number): number {
  const t = Math.max(0, Math.min(1, (value - edge0) / (edge1 - edge0)));
  return t * t * (3 - (2 * t));
}

function mixColor(
  a: [number, number, number],
  b: [number, number, number],
  amount: number,
): [number, number, number] {
  const t = Math.max(0, Math.min(1, amount));
  return [
    a[0] + ((b[0] - a[0]) * t),
    a[1] + ((b[1] - a[1]) * t),
    a[2] + ((b[2] - a[2]) * t),
  ];
}

function getInfernalCapeCornerColors(
  positions: Float32Array,
  indices: number[],
  faceIndexOffset: number,
  faceColor?: number,
): Array<[number, number, number]> | null {
  const corners = [
    (indices[faceIndexOffset] ?? -1) * 3,
    (indices[faceIndexOffset + 1] ?? -1) * 3,
    (indices[faceIndexOffset + 2] ?? -1) * 3,
  ];
  if (corners.some((vertexIndex) => vertexIndex < 0)) {
    return null;
  }

  const centroidY = corners.reduce((sum, vertexIndex) => sum + (positions[vertexIndex + 1] ?? 0), 0) / 3;
  if (centroidY >= 1.18 || [16, 20, 24, 28].includes(faceColor ?? -1)) {
    const trim = centroidY >= 1.18
      ? [0.055, 0.051, 0.05] as [number, number, number]
      : [0.025, 0.023, 0.022] as [number, number, number];
    return [trim, trim, trim];
  }
  if ([3005, 5056, 5066].includes(faceColor ?? -1) && centroidY <= 0.38) {
    const ember = [0.86, 0.42, 0.035] as [number, number, number];
    return [ember, ember, ember];
  }

  return corners.map((vertexIndex) => getInfernalCapeColorAtPosition(
    positions[vertexIndex] ?? 0,
    positions[vertexIndex + 1] ?? 0,
    positions[vertexIndex + 2] ?? 0,
  ));
}

function isInfernalCapeTexturedFace(
  positions: Float32Array,
  indices: number[],
  faceIndexOffset: number,
  faceColor?: number,
): boolean {
  if (faceColor === 0 || [16, 20, 24, 28].includes(faceColor ?? -1)) {
    return false;
  }

  const corners = [
    (indices[faceIndexOffset] ?? -1) * 3,
    (indices[faceIndexOffset + 1] ?? -1) * 3,
    (indices[faceIndexOffset + 2] ?? -1) * 3,
  ];
  if (corners.some((vertexIndex) => vertexIndex < 0)) {
    return false;
  }

  let centroidY = 0;
  let maxAbsX = 0;
  let minZ = Number.POSITIVE_INFINITY;
  for (const vertexIndex of corners) {
    centroidY += positions[vertexIndex + 1] ?? 0;
    maxAbsX = Math.max(maxAbsX, Math.abs(positions[vertexIndex] ?? 0));
    minZ = Math.min(minZ, positions[vertexIndex + 2] ?? 0);
  }
  centroidY /= 3;

  return centroidY >= 0.34
    && centroidY <= 1.16
    && maxAbsX <= 0.27
    && minZ <= -0.065;
}

function getInfernalCapeColorAtPosition(x: number, y: number, z: number): [number, number, number] {
  const u = Math.max(0, Math.min(1, (x + 0.29) / 0.58));
  const v = Math.max(0, Math.min(1, (1.28 - y) / 1.08));
  const surface = Math.max(0, Math.min(1, ((-z) - 0.035) / 0.18));

  const lineA = Math.abs(Math.sin(((u * 2.6) + (v * 4.9) + 0.12) * Math.PI));
  const lineB = Math.abs(Math.sin(((u * 4.7) - (v * 3.35) + 0.37) * Math.PI));
  const lineC = Math.abs(Math.sin(((u * 7.1) + (v * 1.85) + 0.61) * Math.PI));
  const crack = 1 - Math.min(lineA, lineB, lineC);
  const halo = smoothstep(0.62, 0.82, crack) * surface;
  const core = smoothstep(0.84, 0.97, crack) * surface;

  const rock: [number, number, number] = [0.026, 0.025, 0.024];
  const ember: [number, number, number] = [0.45, 0.055, 0.025];
  const lava: [number, number, number] = [0.95, 0.58, 0.045];
  const hotLava: [number, number, number] = [1, 0.78, 0.08];
  const edgeDarken = smoothstep(0.18, 0.28, Math.abs(x));
  const verticalShade = 0.86 + (0.14 * smoothstep(0.15, 0.85, v));

  let color = mixColor(rock, ember, halo * 0.78);
  color = mixColor(color, lava, core * 0.84);
  color = mixColor(color, hotLava, Math.max(0, core - 0.55) * 0.55);
  color = mixColor(color, [0.012, 0.012, 0.012], edgeDarken * 0.55);
  return [
    color[0] * verticalShade,
    color[1] * verticalShade,
    color[2] * verticalShade,
  ];
}

function applySmoothExpandedNormals(
  geometry: THREE.BufferGeometry,
  expandedVertexSources: ArrayLike<number>,
): void {
  const positionAttribute = geometry.getAttribute('position');
  if (!(positionAttribute instanceof THREE.BufferAttribute)) {
    geometry.computeVertexNormals();
    return;
  }

  const positions = positionAttribute.array as Float32Array;
  const normalSums = new Map<number, [number, number, number]>();
  const faceNormal = new THREE.Vector3();
  const edgeAB = new THREE.Vector3();
  const edgeAC = new THREE.Vector3();

  for (let vertexIndex = 0; vertexIndex < expandedVertexSources.length; vertexIndex += 3) {
    const ia = vertexIndex * 3;
    const ib = ia + 3;
    const ic = ia + 6;
    edgeAB.set(
      (positions[ib] ?? 0) - (positions[ia] ?? 0),
      (positions[ib + 1] ?? 0) - (positions[ia + 1] ?? 0),
      (positions[ib + 2] ?? 0) - (positions[ia + 2] ?? 0),
    );
    edgeAC.set(
      (positions[ic] ?? 0) - (positions[ia] ?? 0),
      (positions[ic + 1] ?? 0) - (positions[ia + 1] ?? 0),
      (positions[ic + 2] ?? 0) - (positions[ia + 2] ?? 0),
    );
    faceNormal.crossVectors(edgeAB, edgeAC);
    if (faceNormal.lengthSq() <= 1e-10) {
      continue;
    }

    for (let corner = 0; corner < 3; corner += 1) {
      const sourceVertex = expandedVertexSources[vertexIndex + corner] ?? -1;
      if (sourceVertex < 0) {
        continue;
      }
      const existing = normalSums.get(sourceVertex) ?? [0, 0, 0];
      existing[0] += faceNormal.x;
      existing[1] += faceNormal.y;
      existing[2] += faceNormal.z;
      normalSums.set(sourceVertex, existing);
    }
  }

  const normals = new Float32Array(expandedVertexSources.length * 3);
  for (let vertexIndex = 0; vertexIndex < expandedVertexSources.length; vertexIndex += 1) {
    const sourceVertex = expandedVertexSources[vertexIndex] ?? -1;
    const sum = normalSums.get(sourceVertex);
    if (!sum) {
      normals[vertexIndex * 3] = 0;
      normals[(vertexIndex * 3) + 1] = 1;
      normals[(vertexIndex * 3) + 2] = 0;
      continue;
    }

    const length = Math.hypot(sum[0], sum[1], sum[2]);
    if (length <= 1e-8) {
      normals[vertexIndex * 3] = 0;
      normals[(vertexIndex * 3) + 1] = 1;
      normals[(vertexIndex * 3) + 2] = 0;
      continue;
    }
    normals[vertexIndex * 3] = sum[0] / length;
    normals[(vertexIndex * 3) + 1] = sum[1] / length;
    normals[(vertexIndex * 3) + 2] = sum[2] / length;
  }

  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
}

function computeFaceNudge(
  positions: Float32Array,
  indices: number[],
  faceIndexOffset: number,
  magnitude: number,
): [number, number, number] | null {
  const ia = (indices[faceIndexOffset] ?? -1) * 3;
  const ib = (indices[faceIndexOffset + 1] ?? -1) * 3;
  const ic = (indices[faceIndexOffset + 2] ?? -1) * 3;
  if (ia < 0 || ib < 0 || ic < 0) {
    return null;
  }

  const ax = positions[ia] ?? 0;
  const ay = positions[ia + 1] ?? 0;
  const az = positions[ia + 2] ?? 0;
  const bx = positions[ib] ?? 0;
  const by = positions[ib + 1] ?? 0;
  const bz = positions[ib + 2] ?? 0;
  const cx = positions[ic] ?? 0;
  const cy = positions[ic + 1] ?? 0;
  const cz = positions[ic + 2] ?? 0;

  const abx = bx - ax;
  const aby = by - ay;
  const abz = bz - az;
  const acx = cx - ax;
  const acy = cy - ay;
  const acz = cz - az;

  let nx = aby * acz - abz * acy;
  let ny = abz * acx - abx * acz;
  let nz = abx * acy - aby * acx;
  const length = Math.hypot(nx, ny, nz);
  if (length <= 1e-8) {
    return null;
  }

  nx /= length;
  ny /= length;
  nz /= length;

  const centroidX = (ax + bx + cx) / 3;
  const centroidZ = (az + bz + cz) / 3;
  if (nx * centroidX + nz * centroidZ < 0) {
    nx = -nx;
    ny = -ny;
    nz = -nz;
  }

  return [nx * magnitude, ny * magnitude, nz * magnitude];
}

function collectHiddenAppearanceSlots(
  equipment: Partial<Record<number, EquippedAppearanceItem | null | undefined>>,
  items: Record<string, CharacterItemAppearance>,
  models: Record<string, CharacterModelEntry>,
  animationStrategy: CharacterAnimationStrategy,
): Set<number> {
  const hidden = new Set<number>();
  for (const slot of EQUIPMENT_SLOTS) {
    const equipped = equipment[slot];
    if (!equipped) {
      continue;
    }
    const appearance = items[String(equipped.id)];
    for (const appearanceSlot of getHiddenAppearanceSlotsForItem(equipped, appearance, models, animationStrategy)) {
      hidden.add(appearanceSlot);
    }
  }
  return hidden;
}

function getRenderableEquipment(
  equipment: Partial<Record<number, EquippedAppearanceItem | null | undefined>>,
  items: Record<string, CharacterItemAppearance>,
): EquippedAppearanceItem[] {
  return EQUIPMENT_SLOTS
    .map((slot) => {
      const item = equipment[slot];
      return item ? { id: item.id, slot } : null;
    })
    .filter((item): item is EquippedAppearanceItem => item !== null)
    .filter((item) => VISIBLE_RENDER_SLOTS.has(item.slot))
    .filter((item) => {
      const appearance = items[String(item.id)];
      return Boolean(appearance && appearance.male.bodyModelKeys.length > 0);
    })
    .sort((left, right) => getRenderPriority(left.slot) - getRenderPriority(right.slot));
}

function getRenderPriority(slot: number): number {
  const order = [
    EquipSlot.CAPE,
    EquipSlot.NECK,
    EquipSlot.LEGS,
    EquipSlot.FEET,
    EquipSlot.HANDS,
    EquipSlot.BODY,
    EquipSlot.HEAD,
    EquipSlot.AMMO,
    EquipSlot.WEAPON,
    EquipSlot.SHIELD,
    EquipSlot.RING,
  ];

  const index = order.indexOf(slot);
  return index >= 0 ? index : order.length + slot;
}

function getMaterialStyleForItem(item: CharacterItemAppearance): MaterialStyle {
  const lowerName = item.name.toLowerCase();

  switch (item.slot) {
    case EquipSlot.CAPE:
      return refineMaterialStyle(
        {
          roughness: 0.98,
          metalness: 0.01,
          emissiveIntensity: 0.035,
          doubleSided: true,
          smoothNormals: true,
          radialOutset: 0.052,
          radialOutsetTopY: 1.24,
          radialOutsetHeight: 0.92,
          radialOutsetMinFactor: 0.34,
        },
        lowerName,
      );
    case EquipSlot.NECK:
      return refineMaterialStyle(
        {
          roughness: 0.72,
          metalness: 0.18,
          emissiveIntensity: 0.055,
          polygonOffset: true,
          polygonOffsetFactor: -6,
          polygonOffsetUnits: -6,
          nudgeAllFaces: true,
          faceNudgeAmount: 0.016,
          depthWrite: false,
        },
        lowerName,
      );
    case EquipSlot.WEAPON:
    case EquipSlot.SHIELD:
      return refineMaterialStyle({ roughness: 0.68, metalness: 0.24, emissiveIntensity: 0.06 }, lowerName);
    case EquipSlot.HEAD:
    case EquipSlot.BODY:
    case EquipSlot.LEGS:
      return refineMaterialStyle({ roughness: 0.78, metalness: 0.12, emissiveIntensity: 0.045 }, lowerName);
    case EquipSlot.HANDS:
    case EquipSlot.FEET:
      return refineMaterialStyle({ roughness: 0.88, metalness: 0.07, emissiveIntensity: 0.03 }, lowerName);
    default:
      return BASE_KIT_MATERIAL;
  }
}

function getEquipmentColorSwaps(item: CharacterItemAppearance): CharacterColorSwap[] {
  const swaps = [...(item.recolors ?? [])];
  const lowerName = item.name.toLowerCase();

  if (lowerName === "dharok's platebody") {
    swaps.push({ from: 4550, to: 10388 });
    swaps.push({ from: 7731, to: 10388 });
    swaps.push({ from: 8656, to: 10388 });
  }
  if (lowerName === "dharok's platelegs") {
    swaps.push({ from: 4550, to: 10512 });
  }

  return swaps;
}

function rgb255(red: number, green: number, blue: number): [number, number, number] {
  return [red / 255, green / 255, blue / 255];
}

function cleanSkillcapePaletteOverrides(
  body: {
    main: [number, number, number];
    shadow: [number, number, number];
    highlight: [number, number, number];
  },
  trim: {
    main: [number, number, number];
    shadow: [number, number, number];
    highlight: [number, number, number];
  },
): Record<number, [number, number, number]> {
  return {
    57280: body.highlight,
    54183: body.shadow,
    54503: body.main,
    920: body.main,
    796: trim.highlight,
    908: trim.shadow,
    6067: trim.highlight,
    6563: trim.main,
    7104: trim.main,
    7116: trim.main,
    9152: trim.main,
    11224: trim.main,
    49: trim.shadow,
    51: trim.main,
    103: trim.main,
    28: trim.highlight,
    37: trim.shadow,
  };
}

function isWeaponPrimaryEquipment(item: CharacterItemAppearance): boolean {
  return item.slot === EquipSlot.WEAPON || item.wearPos?.primary === EquipSlot.WEAPON;
}

function getEquipmentDisplayOffset(
  item: CharacterItemAppearance,
  override?: EquipmentPresentationOverride,
): THREE.Vector3 {
  const lowerName = item.name.toLowerCase();

  let offset: THREE.Vector3;
  switch (item.slot) {
    case EquipSlot.CAPE:
      offset = new THREE.Vector3(
        0,
        /infernal|assembler|max cape|fire cape/.test(lowerName) ? 0.014 : 0.008,
        /infernal|assembler|max cape|fire cape/.test(lowerName) ? -0.068 : -0.052,
      );
      break;
    case EquipSlot.NECK:
      offset = new THREE.Vector3(0, 0.014, -0.19);
      break;
    case EquipSlot.WEAPON:
      if (/godsword|maul|scythe|bulwark|2h|greataxe/.test(lowerName)) {
        offset = new THREE.Vector3(0.024, 0.011, 0.029);
        break;
      }
      if (/staff|shadow|trident|wand/.test(lowerName)) {
        offset = new THREE.Vector3(0.021, 0.01, 0.028);
        break;
      }
      if (/bow|crossbow|ballista|webweaver|zaryte/.test(lowerName)) {
        offset = new THREE.Vector3(0.023, 0.009, 0.026);
        break;
      }
      if (/spear|hasta|halberd/.test(lowerName)) {
        offset = new THREE.Vector3(0.019, 0.008, 0.024);
        break;
      }
      if (/claws|dagger|whip|voidwaker/.test(lowerName)) {
        offset = new THREE.Vector3(0.012, 0.006, 0.008);
        break;
      }
      offset = new THREE.Vector3(0.015, 0.007, 0.011);
      break;
    case EquipSlot.SHIELD:
      if (/defender|buckler/.test(lowerName)) {
        offset = new THREE.Vector3(-0.011, 0.003, 0.018);
        break;
      }
      if (/ward|spirit shield|elysian|divine/.test(lowerName)) {
        offset = new THREE.Vector3(-0.022, 0.005, 0.015);
        break;
      }
      offset = new THREE.Vector3(-0.016, 0.004, 0.013);
      break;
    case EquipSlot.HEAD:
      // Helm sits at native origin — no Z push. Earlier -0.04 over-corrected
      // and the helm ended up ~0.08 BEHIND the body torso centroid in
      // measurements. Zero offset puts it close to the head kit position.
      offset = new THREE.Vector3(0, 0, 0);
      break;
    case EquipSlot.BODY:
      // OSRS-LIVE platebody mesh is ~0.12 units forward of the animated
      // body kits in world space (body.scale=1.92, so ~0.06 in local
      // units). The rigid-attachment system can't auto-correct because
      // anchor slot 2 (Torso) is hidden when wearing a platebody, so
      // rest/current centroids are identical and the diff is zero.
      // Empirically tuned on the dev 3d-test grid: -0.10 lands the
      // platebody flush on the torso silhouette across iron / rune /
      // dharok / torva. Robes drape outward so they get a smaller pull.
      offset = new THREE.Vector3(
        0,
        0,
        /robe|vestment|ancestral|virtus|mystic|ahrim/.test(lowerName) ? -0.085 : -0.10,
      );
      break;
    case EquipSlot.LEGS:
      offset = new THREE.Vector3(0, 0, /robe|skirt|chaps/.test(lowerName) ? 0.004 : 0);
      break;
    case EquipSlot.FEET:
      // Boots sit at native origin — no Z push. The earlier -0.04 was
      // measured to land them ~0.05 BEHIND the body kit feet. Zero
      // offset is the better default; per-boot overrides go in the
      // resolveEquipmentPresentationOverride name table if a specific
      // boot mesh diverges.
      offset = new THREE.Vector3(0, 0, 0);
      break;
    default:
      offset = new THREE.Vector3();
      break;
  }

  return applyOffsetOverride(offset, override?.offset);
}

function getNativeWeaponDisplayOffset(
  item: CharacterItemAppearance,
  override?: EquipmentPresentationOverride,
): THREE.Vector3 {
  const nativeOffset = override?.nativeOffset;
  return nativeOffset
    ? new THREE.Vector3(nativeOffset[0], nativeOffset[1], nativeOffset[2])
    : new THREE.Vector3();
}

function getNativeEquipmentMirror(
  item: CharacterItemAppearance,
  override?: EquipmentPresentationOverride,
): [boolean, boolean, boolean] | undefined {
  return override?.nativeMirror;
}

function getNativeWeaponDisplayRotation(
  override?: EquipmentPresentationOverride,
): THREE.Euler {
  const rotation = override?.nativeRotation;
  return rotation
    ? new THREE.Euler(rotation[0], rotation[1], rotation[2])
    : new THREE.Euler();
}

function getEquipmentDisplayRotation(
  item: CharacterItemAppearance,
  override?: EquipmentPresentationOverride,
): THREE.Euler {
  const lowerName = item.name.toLowerCase();

  let rotation: THREE.Euler;
  switch (item.slot) {
    case EquipSlot.CAPE:
      rotation = new THREE.Euler(0.012, 0, 0);
      break;
    case EquipSlot.NECK:
      rotation = new THREE.Euler(-0.25, 0, 0);
      break;
    case EquipSlot.WEAPON:
      if (/godsword|maul|scythe|bulwark|2h|greataxe/.test(lowerName)) {
        rotation = new THREE.Euler(0.02, -0.08, -0.1);
        break;
      }
      if (/staff|shadow|trident|wand/.test(lowerName)) {
        rotation = new THREE.Euler(0, -0.04, -0.06);
        break;
      }
      if (/bow|crossbow|ballista|webweaver|zaryte/.test(lowerName)) {
        rotation = new THREE.Euler(0.01, -0.04, -0.12);
        break;
      }
      if (/spear|hasta|halberd/.test(lowerName)) {
        rotation = new THREE.Euler(0, -0.02, -0.08);
        break;
      }
      rotation = new THREE.Euler(0, 0, -0.05);
      break;
    case EquipSlot.SHIELD:
      if (/defender/.test(lowerName)) {
        rotation = new THREE.Euler(0, 0.08, 0.04);
        break;
      }
      rotation = new THREE.Euler(0, 0.05, 0.02);
      break;
    default:
      rotation = new THREE.Euler();
      break;
  }

  return applyRotationOverride(rotation, override?.rotation);
}

function getNativeEquipmentDisplayRotation(): THREE.Euler {
  return new THREE.Euler();
}

function refineMaterialStyle(base: MaterialStyle, lowerName: string): MaterialStyle {
  const style = { ...base };
  const isCapeOfAccomplishment = /(?:attack|strength|defence|ranging|prayer|magic|runecraft|hitpoints|agility|herblore|thieving|crafting|fletching|slayer|construct\.?|construction|mining|smithing|fishing|cooking|firemaking|woodcut\.?|woodcutting|farming|hunter|sailing|summoning) cape(?:\(t\))?$/.test(lowerName);

  if (/torva|bandos|dragon|rune|armadyl|justiciar|avernic|barrows|inquisitor/.test(lowerName)) {
    style.roughness = Math.max(0.56, style.roughness - 0.14);
    style.metalness = Math.min(0.34, style.metalness + 0.1);
    style.emissiveIntensity = Math.min(0.09, style.emissiveIntensity + 0.02);
  }

  if (/masori|zaryte|venator|assembler|voidwaker/.test(lowerName)) {
    style.roughness = Math.max(0.62, style.roughness - 0.08);
    style.metalness = Math.min(0.26, style.metalness + 0.05);
    style.emissiveIntensity = Math.min(0.085, style.emissiveIntensity + 0.015);
  }

  if (/virtus|ancestral|mystic|ahrim|robe|cape|void|elder chaos/.test(lowerName)) {
    style.roughness = Math.max(style.roughness, 0.9);
    style.metalness = Math.min(style.metalness, 0.05);
    style.emissiveIntensity = Math.min(0.08, style.emissiveIntensity + 0.015);
    style.doubleSided = style.doubleSided ?? /cape|robe/.test(lowerName);
  }

  if (/bow|crossbow|ballista|staff|wand|trident/.test(lowerName)) {
    style.roughness = Math.max(style.roughness, 0.78);
    style.metalness = Math.min(style.metalness, 0.14);
  }

  if (/crystal|sanguinesti|shadow|tumeken/.test(lowerName)) {
    style.emissiveIntensity = Math.min(0.1, style.emissiveIntensity + 0.025);
  }

  if (lowerName === 'fire cape') {
    style.roughness = 0.74;
    style.metalness = 0.02;
    style.emissiveIntensity = 0.12;
    style.doubleSided = true;
    style.texturePaths = {
      40: '/assets/character/textures/osrs-live-texture-485.png',
    };
    style.faceColorOverrides = {
      127: [1, 0.72, 0.18],
      924: [0.82, 0.18, 0.04],
      6032: [0.07, 0.06, 0.05],
    };
    // Fire cape models include one loose untextured cap triangle at the end of
    // the mesh. Male extracts color it yellow (HSL 127), female extracts color
    // it black (HSL 0); both read as a floating shard in posed profile renders.
    style.skipUntexturedFaceColors = [0, 127];
    // In static profile poses the real OSRS cape rig follows 2H stances, but
    // platebodies/legs can sit on the same depth plane and punch through it.
    // Push the animated cape surface smoothly away from the body center after
    // the stance is applied, preserving texture UVs and avoiding per-face gaps.
    style.radialOutset = 0.06;
    style.radialOutsetTopY = 1.24;
    style.radialOutsetHeight = 0.9;
    style.radialOutsetMinFactor = 0.35;
  }

  if (lowerName === 'infernal cape') {
    style.roughness = 0.82;
    style.metalness = 0.02;
    style.emissiveIntensity = 0.14;
    style.doubleSided = true;
    style.infernalLavaPattern = true;
    style.texturePaths = {
      59: '/assets/character/textures/osrs-live-texture-318.png',
    };
    style.skipUntexturedFaceColors = [0];
    style.smoothNormals = true;
    style.nudgePriorityFaces = true;
    style.priorityFaceNudgeAmount = 0.0035;
    style.radialOutset = 0.044;
    style.radialOutsetTopY = 1.26;
    style.radialOutsetHeight = 0.9;
    style.radialOutsetMinFactor = 0.32;
  }

  if (isCapeOfAccomplishment) {
    style.roughness = Math.min(style.roughness, 0.88);
    style.emissiveIntensity = Math.max(style.emissiveIntensity, 0.06);
    style.smoothNormals = true;
    style.nudgeFlatOverlayFaces = false;
    style.nudgePriorityFaces = false;
    style.capeEmblemOverlayFaces = true;
    style.capeEmblemOverlayNudgeAmount = 0.016;
    // Official skillcape models already contain their back panel and trim.
    // Duplicating broad lower faces as an always-on-top fill hides the side
    // trim and cuts through several skill icons, so keep the model geometry
    // single-pass and rely on radial outset for body/leg separation.
    style.depthFillLowerCape = false;
    style.skipLargeCapeArtifactFaces = true;
    style.radialOutset = Math.max(style.radialOutset ?? 0, 0.096);
    style.radialOutsetTopY = 1.24;
    style.radialOutsetHeight = 0.92;
    style.radialOutsetMinFactor = 0.58;
  }

  if (lowerName === 'sailing cape' || lowerName === 'sailing cape(t)') {
    const sailingBody = {
      main: rgb255(11, 46, 72),
      shadow: rgb255(7, 29, 48),
      highlight: rgb255(20, 90, 132),
    };
    const sailingTrim = lowerName === 'sailing cape(t)'
      ? {
          main: rgb255(207, 168, 70),
          shadow: rgb255(124, 82, 30),
          highlight: rgb255(247, 219, 119),
        }
      : {
          main: rgb255(43, 143, 190),
          shadow: rgb255(7, 29, 48),
          highlight: rgb255(54, 166, 215),
        };
    style.faceColorOverrides = cleanSkillcapePaletteOverrides(sailingBody, sailingTrim);
    style.texturePaths = {
      ...(style.texturePaths ?? {}),
      9001: '/sprites/skill/sailing.png',
    };
  }

  if (lowerName === 'summoning cape' || lowerName === 'summoning cape(t)') {
    const summoningBody = {
      main: rgb255(132, 168, 200),
      shadow: rgb255(75, 95, 120),
      highlight: rgb255(227, 241, 248),
    };
    const summoningTrim = lowerName === 'summoning cape(t)'
      ? {
          main: rgb255(216, 198, 107),
          shadow: rgb255(149, 116, 44),
          highlight: rgb255(240, 220, 117),
        }
      : {
          main: rgb255(227, 241, 248),
          shadow: rgb255(112, 131, 143),
          highlight: rgb255(240, 248, 252),
        };
    style.faceColorOverrides = cleanSkillcapePaletteOverrides(summoningBody, summoningTrim);
  }

  return style;
}

function getHiddenAppearanceSlotsForItem(
  item: EquippedAppearanceItem,
  appearance?: CharacterItemAppearance,
  models?: Record<string, CharacterModelEntry>,
  animationStrategy: CharacterAnimationStrategy = 'current',
): number[] {
  if (!itemAppearanceHasRenderableModel(appearance, models)) {
    return [];
  }

  const animationMode = appearance && models
    ? getEquipmentAnimationMode(appearance, models, animationStrategy)
    : 'rigid';
  const hasAnimatableRig = animationMode === 'full';

  if (item.slot === EquipSlot.BODY) {
    // Platebodies cover the torso and arms, but their wear models do not
    // include bare hands. Gloves still hide/replace hand kits through slot 9.
    // body kits when a body item is equipped — gloves re-add a hand mesh
    return shouldBodyItemHideArms(appearance) ? [2, 3] : [2];
  }

  if (item.slot === EquipSlot.FEET) {
    return [6];
  }

  if (item.slot === EquipSlot.HANDS) {
    return shouldHandItemHideHands(appearance) ? [4] : [];
  }

  // Static profile / equipment renders only need a clean posed silhouette, not
  // per-vertex armor deformation. In that mode we still want the normal body
  // coverage rules even when the item is attached rigidly as a whole mesh.
  const allowRigidCoverage = animationStrategy === 'static-pose';

  if (RIG_DEPENDENT_COVERAGE_SLOTS.has(item.slot) && !hasAnimatableRig && !allowRigidCoverage) {
    return [];
  }

  if (item.slot !== EquipSlot.HEAD) {
    return BODY_SLOT_TO_APPEARANCE_SLOTS[item.slot] ?? [];
  }

  if (isFullHeadCoverageItem(appearance)) {
    return [0, 1];
  }

  if (isHatStyleHeadItem(appearance)) {
    return [];
  }

  return [0];
}

function isFullHeadCoverageItem(appearance?: CharacterItemAppearance): boolean {
  if (!appearance) {
    return false;
  }

  const lowerName = appearance.name.toLowerCase();
  if (isFacePreservingHeadItemName(lowerName) || appearance.equipmentType === 'HAT') {
    return false;
  }

  if (appearance.equipmentType === 'FULL_HELMET' || appearance.equipmentType === 'MASK') {
    return true;
  }

  return /full helm|full helmet|faceguard|mask|slayer helmet|great helm/i.test(appearance.name);
}

function isHatStyleHeadItem(appearance?: CharacterItemAppearance): boolean {
  if (!appearance) {
    return false;
  }

  const equipmentType = appearance.equipmentType ?? '';
  const lowerName = appearance.name.toLowerCase();
  return equipmentType === 'HAT'
    || isFacePreservingHeadItemName(lowerName)
    || /hat|cap|beret|boater|cavalier|fedora|fez|mitre|partyhat|santa/i.test(appearance.name);
}

function isFacePreservingHeadItemName(lowerName: string): boolean {
  return lowerName === 'highwayman mask'
    || lowerName === 'sunfire fanatic helm'
    || lowerName === 'blood moon helm'
    || lowerName === 'eclipse moon helm';
}

function shouldBodyItemHideArms(appearance?: CharacterItemAppearance): boolean {
  if (!appearance) {
    return false;
  }

  if (hasWearPosCoverage(appearance, ARM_COVERAGE_WEAR_POS)) {
    return true;
  }

  return /platebody|chestplate|hauberk|robe top|robe|cuirass|vestment|torva|justiciar|virtus|ancestral|ahrim|mystic/i.test(
    appearance.name,
  );
}

function shouldHandItemHideHands(appearance?: CharacterItemAppearance): boolean {
  if (!appearance) {
    return false;
  }

  if (appearance.equipmentType === 'GLOVES') {
    return true;
  }

  return /gloves|gauntlets|vambraces/i.test(appearance.name);
}

function itemAppearanceHasAnimationRig(
  appearance: CharacterItemAppearance,
  models: Record<string, CharacterModelEntry>,
): boolean {
  const threshold = getMinimumRigGroupCountForAppearance(appearance);
  const keys = appearance.male.bodyModelKeys.filter(Boolean);
  if (keys.length === 0) {
    return false;
  }

  const checks = keys.map((modelKey) => {
    const model = models[modelKey];
    return modelHasUsableAnimationRig(model, threshold);
  });

  if (appearance.slot === EquipSlot.BODY || appearance.slot === EquipSlot.LEGS || appearance.slot === EquipSlot.HANDS) {
    return checks.every(Boolean);
  }

  return checks.some(Boolean);
}

function itemAppearanceHasRenderableModel(
  appearance?: CharacterItemAppearance,
  models?: Record<string, CharacterModelEntry>,
): boolean {
  if (!appearance || !models) {
    return false;
  }

  return appearance.male.bodyModelKeys.some((modelKey) => Boolean(models[modelKey]));
}

function getEquipmentAnimationMode(
  appearance: CharacterItemAppearance,
  models: Record<string, CharacterModelEntry>,
  animationStrategy: CharacterAnimationStrategy = 'current',
  override?: EquipmentPresentationOverride,
): AnimationMode {
  if (override?.mode === 'full' || override?.mode === 'rigid') {
    return override.mode;
  }

  const hasUsableRig = itemAppearanceHasAnimationRig(appearance, models);
  if (
    animationStrategy === 'static-pose'
    && hasUsableRig
    && (appearance.slot === EquipSlot.CAPE || appearance.slot === EquipSlot.NECK)
  ) {
    return 'full';
  }

  if (shouldForceRigidEquipmentMotion(appearance, animationStrategy)) {
    return 'rigid';
  }

  return hasUsableRig ? 'full' : 'rigid';
}

function shouldForceRigidEquipmentMotion(
  appearance: CharacterItemAppearance,
  animationStrategy: CharacterAnimationStrategy,
): boolean {
  const lowerName = appearance.name.toLowerCase();
  const slot = appearance.slot;
  switch (animationStrategy) {
    case 'body-only':
      return true;
    case 'static-pose':
      return slot === EquipSlot.CAPE
        || (slot === EquipSlot.SHIELD && appearance.wearPos?.primary !== EquipSlot.WEAPON);
    case 'armor-deform':
    case 'armor-deform-all-pivots':
      return slot === EquipSlot.CAPE || slot === EquipSlot.WEAPON || slot === EquipSlot.SHIELD;
    case 'full-if-rigged':
      return slot === EquipSlot.WEAPON;
    case 'current':
    default:
      return (slot != null && FORCE_RIGID_EQUIPMENT_SLOTS.has(slot))
        || FORCE_RIGID_EQUIPMENT_NAMES.has(lowerName);
  }
}

function modelHasUsableAnimationRig(model: CharacterModelEntry | undefined, minimumGroupCount = DEFAULT_MIN_EQUIPMENT_RIG_GROUPS): boolean {
  if (!model || !Array.isArray(model.vertexGroups)) {
    return false;
  }

  const nonEmptyGroups = model.vertexGroups.reduce((count, group) => (
    Array.isArray(group) && group.length > 0 ? count + 1 : count
  ), 0);

  return nonEmptyGroups >= minimumGroupCount;
}

function resolveCharacterAnimationStrategy(
  strategy?: CharacterAnimationStrategy,
): CharacterAnimationStrategy {
  return strategy ?? 'current';
}

function usesAllPivotParts(strategy: CharacterAnimationStrategy): boolean {
  return strategy === 'armor-deform-all-pivots';
}

function initializeRigidAttachments(body: THREE.Group): void {
  const restCenterMaps = collectAppearanceSlotCenterMaps(body);
  const restCenters = restCenterMaps.all;
  body.userData.rigidAttachmentRestCenters = Object.fromEntries(
    [...restCenters.entries()].map(([slot, center]) => [String(slot), center.toArray()]),
  );

  body.traverse((entry) => {
    if (!(entry instanceof THREE.Group)) {
      return;
    }

    const attachment = entry.userData.rigidAttachment as RigidAttachmentState | undefined;
    if (!attachment) {
      return;
    }

    const restAnchor = resolveAttachmentCenterFromMaps(restCenterMaps, attachment.anchorSlots, attachment.anchorSide);
    const restAim = resolveAttachmentCenterFromMaps(restCenterMaps, attachment.aimSlots, attachment.aimSide);
    attachment.restAnchor = restAnchor ? (restAnchor.toArray() as [number, number, number]) : undefined;
    attachment.restAim = restAim ? (restAim.toArray() as [number, number, number]) : undefined;
  });

  updateRigidAttachments(body);
}

function updateRigidAttachments(body: THREE.Group): void {
  const currentCenterMaps = collectAppearanceSlotCenterMaps(body);
  const currentCenters = currentCenterMaps.all;
  const restCenters = resolveStoredAppearanceSlotCenters(body) ?? currentCenters;

  body.traverse((entry) => {
    if (!(entry instanceof THREE.Group)) {
      return;
    }

    const attachment = entry.userData.rigidAttachment as RigidAttachmentState | undefined;
    if (!attachment) {
      return;
    }

    const basePosition = new THREE.Vector3().fromArray(attachment.restPosition);
    const baseQuaternion = new THREE.Quaternion().fromArray(attachment.restQuaternion);
    const restAnchor = attachment.restAnchor
      ? new THREE.Vector3().fromArray(attachment.restAnchor)
      : resolveAttachmentCenter(restCenters, attachment.anchorSlots);
    const currentAnchor = resolveAttachmentCenterFromMaps(
      currentCenterMaps,
      attachment.anchorSlots,
      attachment.anchorSide,
    );

    entry.position.copy(basePosition);
    entry.quaternion.copy(baseQuaternion);

    if (restAnchor && currentAnchor) {
      entry.position.add(currentAnchor.clone().sub(restAnchor));
    }

    const restAim = attachment.restAim
      ? new THREE.Vector3().fromArray(attachment.restAim)
      : resolveAttachmentCenter(restCenters, attachment.aimSlots);
    const currentAim = resolveAttachmentCenterFromMaps(
      currentCenterMaps,
      attachment.aimSlots,
      attachment.aimSide,
    );

    if (restAnchor && currentAnchor && restAim && currentAim) {
      const restDirection = restAim.clone().sub(restAnchor);
      const currentDirection = currentAim.clone().sub(currentAnchor);
      if (restDirection.lengthSq() > 1e-6 && currentDirection.lengthSq() > 1e-6) {
        const alignQuaternion = new THREE.Quaternion().setFromUnitVectors(
          restDirection.normalize(),
          currentDirection.normalize(),
        );
        entry.quaternion.copy(alignQuaternion.multiply(baseQuaternion));
      }
    }
  });
}

function resolveStoredAppearanceSlotCenters(body: THREE.Group): Map<number, THREE.Vector3> | null {
  const stored = body.userData.rigidAttachmentRestCenters as Record<string, number[] | undefined> | undefined;
  if (!stored) {
    return null;
  }

  const centers = new Map<number, THREE.Vector3>();
  for (const [slot, value] of Object.entries(stored)) {
    if (Array.isArray(value) && value.length >= 3) {
      centers.set(Number(slot), new THREE.Vector3(value[0], value[1], value[2]));
    }
  }
  return centers;
}

function collectAppearanceSlotCentroids(body: THREE.Group): Map<number, THREE.Vector3> {
  return collectAppearanceSlotCenterMaps(body).all;
}

function collectAppearanceSlotCenterMaps(body: THREE.Group): AppearanceSlotCenterMaps {
  const all = new Map<number, { sum: THREE.Vector3; count: number }>();
  const negativeX = new Map<number, { sum: THREE.Vector3; count: number }>();
  const positiveX = new Map<number, { sum: THREE.Vector3; count: number }>();

  body.traverse((entry) => {
    if (!(entry instanceof THREE.Mesh)) {
      return;
    }

    const appearanceSlot = entry.userData.appearanceSlot;
    if (typeof appearanceSlot !== 'number') {
      return;
    }

    const position = entry.geometry.getAttribute('position');
    if (!(position instanceof THREE.BufferAttribute)) {
      return;
    }

    for (let index = 0; index < position.count; index += 1) {
      const x = position.getX(index);
      const y = position.getY(index);
      const z = position.getZ(index);
      addAppearanceSlotCenterSample(all, appearanceSlot, x, y, z);
      if (x < 0) {
        addAppearanceSlotCenterSample(negativeX, appearanceSlot, x, y, z);
      } else if (x > 0) {
        addAppearanceSlotCenterSample(positiveX, appearanceSlot, x, y, z);
      }
    }
  });

  return {
    all: finalizeAppearanceSlotCenters(all),
    negativeX: finalizeAppearanceSlotCenters(negativeX),
    positiveX: finalizeAppearanceSlotCenters(positiveX),
  };
}

function addAppearanceSlotCenterSample(
  accumulators: Map<number, { sum: THREE.Vector3; count: number }>,
  appearanceSlot: number,
  x: number,
  y: number,
  z: number,
): void {
  let accumulator = accumulators.get(appearanceSlot);
  if (!accumulator) {
    accumulator = { sum: new THREE.Vector3(), count: 0 };
    accumulators.set(appearanceSlot, accumulator);
  }

  accumulator.sum.x += x;
  accumulator.sum.y += y;
  accumulator.sum.z += z;
  accumulator.count += 1;
}

function finalizeAppearanceSlotCenters(
  accumulators: Map<number, { sum: THREE.Vector3; count: number }>,
): Map<number, THREE.Vector3> {
  const centers = new Map<number, THREE.Vector3>();
  for (const [slot, accumulator] of accumulators.entries()) {
    if (accumulator.count <= 0) {
      continue;
    }
    centers.set(slot, accumulator.sum.multiplyScalar(1 / accumulator.count));
  }
  return centers;
}

function resolveAttachmentCenterFromMaps(
  centers: AppearanceSlotCenterMaps,
  slots: number[],
  side?: AttachmentSide,
): THREE.Vector3 | null {
  if (side === 'negative-x') {
    return resolveAttachmentCenter(centers.negativeX, slots) ?? resolveAttachmentCenter(centers.all, slots);
  }
  if (side === 'positive-x') {
    return resolveAttachmentCenter(centers.positiveX, slots) ?? resolveAttachmentCenter(centers.all, slots);
  }
  return resolveAttachmentCenter(centers.all, slots);
}

function resolveAttachmentCenter(
  centers: Map<number, THREE.Vector3>,
  slots: number[],
): THREE.Vector3 | null {
  const accumulator = new THREE.Vector3();
  let count = 0;
  for (const slot of slots) {
    const center = centers.get(slot);
    if (!center) {
      continue;
    }
    accumulator.add(center);
    count += 1;
  }

  return count > 0 ? accumulator.multiplyScalar(1 / count) : null;
}

function getRigidAnchorSlots(appearance: CharacterItemAppearance): number[] {
  switch (appearance.slot) {
    case EquipSlot.HEAD:
      return [0, 1];
    case EquipSlot.CAPE:
      return [2, 3];
    case EquipSlot.BODY:
      return [2];
    case EquipSlot.WEAPON:
      return [4];
    case EquipSlot.SHIELD:
    case EquipSlot.HANDS:
      return [3, 4];
    case EquipSlot.LEGS:
      return [5];
    case EquipSlot.FEET:
      return [5, 6];
    default:
      return [2];
  }
}

function getRigidAimSlots(appearance: CharacterItemAppearance): number[] {
  switch (appearance.slot) {
    case EquipSlot.HEAD:
      return [2];
    case EquipSlot.CAPE:
      return [5];
    case EquipSlot.BODY:
      return [5];
    case EquipSlot.WEAPON:
      return [3];
    case EquipSlot.SHIELD:
    case EquipSlot.HANDS:
      return [2];
    case EquipSlot.LEGS:
      return [3];
    case EquipSlot.FEET:
      return [5];
    default:
      return [3];
  }
}

function getRigidAnchorSide(appearance: CharacterItemAppearance): AttachmentSide | undefined {
  switch (appearance.slot) {
    case EquipSlot.WEAPON:
      return 'negative-x';
    case EquipSlot.SHIELD:
      return 'positive-x';
    default:
      return undefined;
  }
}

function getRigidAimSide(appearance: CharacterItemAppearance): AttachmentSide | undefined {
  switch (appearance.slot) {
    case EquipSlot.WEAPON:
      return 'negative-x';
    case EquipSlot.SHIELD:
      return 'positive-x';
    default:
      return undefined;
  }
}

function getMinimumRigGroupCountForSlot(slot: number | null | undefined): number {
  if (typeof slot !== 'number') {
    return DEFAULT_MIN_EQUIPMENT_RIG_GROUPS;
  }

  return MIN_EQUIPMENT_RIG_GROUPS_BY_SLOT[slot] ?? DEFAULT_MIN_EQUIPMENT_RIG_GROUPS;
}

function getMinimumRigGroupCountForAppearance(appearance: CharacterItemAppearance): number {
  if (isHatStyleHeadItem(appearance)) {
    return 1;
  }
  return getMinimumRigGroupCountForSlot(appearance.slot);
}

function hasWearPosCoverage(appearance: CharacterItemAppearance, values: Set<number>): boolean {
  const wearPosValues = [
    appearance.wearPos?.primary ?? null,
    appearance.wearPos?.secondary ?? null,
    appearance.wearPos?.tertiary ?? null,
  ];
  return wearPosValues.some((value) => typeof value === 'number' && values.has(value));
}

export function resolveEquipmentPresentationOverride(
  name: string,
  overrides?: Record<string, EquipmentPresentationOverride>,
): EquipmentPresentationOverride | undefined {
  const key = name.toLowerCase();
  const base = EQUIPMENT_PRESENTATION_OVERRIDES[key];
  const custom = overrides?.[key]
    ?? overrides?.[name]
    ?? Object.entries(overrides ?? {}).find(([overrideName]) => overrideName.toLowerCase() === key)?.[1];
  return custom ? { ...(base ?? {}), ...custom } : base;
}

function applyOffsetOverride(base: THREE.Vector3, override?: [number, number, number]): THREE.Vector3 {
  if (!override) {
    return base;
  }
  return new THREE.Vector3(override[0], override[1], override[2]);
}

function applyRotationOverride(base: THREE.Euler, override?: [number, number, number]): THREE.Euler {
  if (!override) {
    return base;
  }
  return new THREE.Euler(override[0], override[1], override[2]);
}

function collectAnimatableParts(body: THREE.Group): CharacterMeshAnimationData[] {
  const parts: CharacterMeshAnimationData[] = [];
  body.traverse((entry) => {
    if (!(entry instanceof THREE.Mesh)) {
      return;
    }

    const animationData = entry.userData.animationData as CharacterMeshAnimationData | undefined;
    if (animationData) {
      parts.push(animationData);
    }
  });
  return parts;
}

function resolveAnimationFrame(
  sequence: CharacterAnimationSequence,
  elapsedMs: number,
  loop: boolean,
  holdLastFrame: boolean,
): CharacterAnimationFrame | null {
  if (sequence.frames.length === 0 || sequence.totalLength <= 0) {
    return sequence.frames[0] ?? null;
  }

  const frameUnitTime = elapsedMs / 20;
  const safeTime = loop
    ? mod(frameUnitTime, sequence.totalLength)
    : holdLastFrame
      ? Math.max(0, Math.min(frameUnitTime, sequence.totalLength - 0.001))
      : frameUnitTime;

  let accumulator = 0;
  for (const frame of sequence.frames) {
    accumulator += Math.max(1, frame.length);
    if (safeTime < accumulator) {
      return frame;
    }
  }

  return holdLastFrame ? sequence.frames[sequence.frames.length - 1] ?? null : sequence.frames[0] ?? null;
}

function animateParts(
  parts: CharacterMeshAnimationData[],
  transforms: CharacterAnimationTransform[],
  animationStrategy: CharacterAnimationStrategy,
  stabilizedHeadStandId: number | null,
): void {
  for (const part of parts) {
    part.workingNativePositions.set(part.baseNativePositions);
  }

  const headParts = stabilizedHeadStandId !== null
    ? parts.filter((part) => isHeadAnimationPart(part, stabilizedHeadStandId))
    : [];
  const transformParts = stabilizedHeadStandId !== null
    ? parts.filter((part) => !isHeadAnimationPart(part, stabilizedHeadStandId))
    : parts;
  const offsets = { x: 0, y: 0, z: 0 };
  const pivotParts = usesAllPivotParts(animationStrategy)
    ? transformParts
    : transformParts.some((part) => part.pivotSource)
      ? transformParts.filter((part) => part.pivotSource)
      : transformParts;

  for (const transform of transforms) {
    if (transform.type === 0) {
      resolveSharedTransformOffsets(pivotParts, transform, offsets);
      continue;
    }

    for (const part of transformParts) {
      applyTransform(part.workingNativePositions, part.vertexGroups, transform, offsets);
    }
    if (stabilizedHeadStandId !== null && transform.type === 1) {
      for (const part of headParts) {
        applyTransform(part.workingNativePositions, part.vertexGroups, transform, offsets);
      }
    }
  }

  if (stabilizedHeadStandId !== null) {
    applyNativeOffsetToParts(headParts, getHeadStabilizationNativeOffset(stabilizedHeadStandId));
  }

  for (const part of parts) {
    writeAnimatedPart(part);
  }
}

function shouldStabilizeHeadForStand(standId: number): boolean {
  return HEAD_STABILIZED_STAND_ANIMATIONS.has(standId);
}

function isHeadAnimationPart(part: CharacterMeshAnimationData, standId: number): boolean {
  return part.appearanceSlot === 0
    || (standId === 7518 && part.appearanceSlot === 1)
    || isStabilizedHeadEquipmentPart(part);
}

function isStabilizedHeadEquipmentPart(part: CharacterMeshAnimationData): boolean {
  if (part.equipmentSlot !== EquipSlot.HEAD) {
    return false;
  }

  return part.equipmentName === 'grey hat'
    || part.equipmentName === 'void ranger helm';
}

function getHeadStabilizationNativeOffset(standId: number): [number, number, number] {
  return HEAD_STABILIZATION_NATIVE_OFFSETS[standId] ?? [0, 0, 0];
}

function applyNativeOffsetToParts(parts: CharacterMeshAnimationData[], offset: [number, number, number]): void {
  if (offset[0] === 0 && offset[1] === 0 && offset[2] === 0) {
    return;
  }

  for (const part of parts) {
    for (let index = 0; index < part.workingNativePositions.length; index += 3) {
      part.workingNativePositions[index] = (part.workingNativePositions[index] ?? 0) + offset[0];
      part.workingNativePositions[index + 1] = (part.workingNativePositions[index + 1] ?? 0) + offset[1];
      part.workingNativePositions[index + 2] = (part.workingNativePositions[index + 2] ?? 0) + offset[2];
    }
  }
}

function applyRadialOutsetToRenderPosition(
  x: number,
  y: number,
  z: number,
  radialOutset?: number,
  radialOutsetTopY?: number,
  radialOutsetHeight?: number,
  radialOutsetMinFactor?: number,
): [number, number, number] {
  if (!radialOutset || radialOutset <= 0) {
    return [x, y, z];
  }

  const radialLength = Math.hypot(x, z);
  if (radialLength <= 1e-6) {
    return [x, y, z];
  }

  const topY = radialOutsetTopY ?? 1.24;
  const height = Math.max(radialOutsetHeight ?? 0.9, 1e-6);
  const minFactor = radialOutsetMinFactor ?? 0.35;
  const taper = Math.max(minFactor, Math.min(1, (topY - y) / height));
  return [
    x + ((x / radialLength) * radialOutset * taper),
    y,
    z + ((z / radialLength) * radialOutset * taper),
  ];
}

function writeAnimatedPart(part: CharacterMeshAnimationData): void {
  const target = part.positionAttribute.array as Float32Array;
  const nudges = part.renderPositionNudges;
  for (let index = 0; index < part.expandedVertexSources.length; index += 1) {
    const sourceVertexIndex = (part.expandedVertexSources[index] ?? 0) * 3;
    const targetIndex = index * 3;
    let x = (part.workingNativePositions[sourceVertexIndex] ?? 0)
      + (nudges?.[targetIndex] ?? 0);
    let y = -(part.workingNativePositions[sourceVertexIndex + 1] ?? 0)
      + (nudges?.[targetIndex + 1] ?? 0);
    let z = -(part.workingNativePositions[sourceVertexIndex + 2] ?? 0)
      + (nudges?.[targetIndex + 2] ?? 0);

    [x, y, z] = applyRadialOutsetToRenderPosition(
      x,
      y,
      z,
      part.radialOutset,
      part.radialOutsetTopY,
      part.radialOutsetHeight,
      part.radialOutsetMinFactor,
    );

    target[targetIndex] = x;
    target[targetIndex + 1] = y;
    target[targetIndex + 2] = z;
  }
  part.positionAttribute.needsUpdate = true;
  if (part.smoothNormals) {
    applySmoothExpandedNormals(part.geometry, part.expandedVertexSources);
  } else {
    part.geometry.computeVertexNormals();
  }
}

function resolveSharedTransformOffsets(
  parts: CharacterMeshAnimationData[],
  transform: CharacterAnimationTransform,
  offsets: { x: number; y: number; z: number },
): void {
  let count = 0;
  offsets.x = 0;
  offsets.y = 0;
  offsets.z = 0;

  for (const part of parts) {
    for (const groupId of transform.groups) {
      const group = part.vertexGroups[groupId];
      if (!group) continue;

      for (const vertexIndex of group) {
        const index = vertexIndex * 3;
        offsets.x += part.workingNativePositions[index] ?? 0;
        offsets.y += part.workingNativePositions[index + 1] ?? 0;
        offsets.z += part.workingNativePositions[index + 2] ?? 0;
        count += 1;
      }
    }
  }

  if (count > 0) {
    offsets.x = transform.dx / 128 + offsets.x / count;
    offsets.y = transform.dy / 128 + offsets.y / count;
    offsets.z = transform.dz / 128 + offsets.z / count;
  } else {
    offsets.x = transform.dx / 128;
    offsets.y = transform.dy / 128;
    offsets.z = transform.dz / 128;
  }
}

function resetAnimatablePart(part: CharacterMeshAnimationData): void {
  const target = part.positionAttribute.array as Float32Array;
  for (let index = 0; index < part.expandedVertexSources.length; index += 1) {
    const sourceVertexIndex = (part.expandedVertexSources[index] ?? 0) * 3;
    const targetIndex = index * 3;
    target[targetIndex] = part.baseNativePositions[sourceVertexIndex] ?? 0;
    target[targetIndex + 1] = -(part.baseNativePositions[sourceVertexIndex + 1] ?? 0);
    target[targetIndex + 2] = -(part.baseNativePositions[sourceVertexIndex + 2] ?? 0);
  }
  part.positionAttribute.needsUpdate = true;
  part.geometry.computeVertexNormals();
}

function applyTransform(
  vertices: Float32Array,
  vertexGroups: number[][],
  transform: CharacterAnimationTransform,
  offsets: { x: number; y: number; z: number },
): void {
  const { type, groups, dx, dy, dz } = transform;

  if (type === 0) {
    let count = 0;
    offsets.x = 0;
    offsets.y = 0;
    offsets.z = 0;

    for (const groupId of groups) {
      const group = vertexGroups[groupId];
      if (!group) continue;
      for (const vertexIndex of group) {
        const index = vertexIndex * 3;
        offsets.x += vertices[index] ?? 0;
        offsets.y += vertices[index + 1] ?? 0;
        offsets.z += vertices[index + 2] ?? 0;
        count += 1;
      }
    }

    if (count > 0) {
      offsets.x = dx / 128 + offsets.x / count;
      offsets.y = dy / 128 + offsets.y / count;
      offsets.z = dz / 128 + offsets.z / count;
    } else {
      offsets.x = dx / 128;
      offsets.y = dy / 128;
      offsets.z = dz / 128;
    }
    return;
  }

  if (type === 5) {
    return;
  }

  for (const groupId of groups) {
    const group = vertexGroups[groupId];
    if (!group) continue;

    for (const vertexIndex of group) {
      const index = vertexIndex * 3;
      let x = vertices[index] ?? 0;
      let y = vertices[index + 1] ?? 0;
      let z = vertices[index + 2] ?? 0;

      if (type === 1) {
        vertices[index] = x + dx / 128;
        vertices[index + 1] = y + dy / 128;
        vertices[index + 2] = z + dz / 128;
        continue;
      }

      x -= offsets.x;
      y -= offsets.y;
      z -= offsets.z;

      if (type === 2) {
        const angleX = ((dx & 255) * 8 * Math.PI) / 1024;
        const angleY = ((dy & 255) * 8 * Math.PI) / 1024;
        const angleZ = ((dz & 255) * 8 * Math.PI) / 1024;

        if (angleZ !== 0) {
          const sin = Math.sin(angleZ);
          const cos = Math.cos(angleZ);
          const nextX = cos * x + sin * y;
          const nextY = cos * y - sin * x;
          x = nextX;
          y = nextY;
        }

        if (angleX !== 0) {
          const sin = Math.sin(angleX);
          const cos = Math.cos(angleX);
          const nextY = cos * y - sin * z;
          const nextZ = sin * y + cos * z;
          y = nextY;
          z = nextZ;
        }

        if (angleY !== 0) {
          const sin = Math.sin(angleY);
          const cos = Math.cos(angleY);
          const nextX = cos * x + sin * z;
          const nextZ = cos * z - sin * x;
          x = nextX;
          z = nextZ;
        }
      } else if (type === 3) {
        x = (dx * x) / 128;
        y = (dy * y) / 128;
        z = (dz * z) / 128;
      }

      vertices[index] = x + offsets.x;
      vertices[index + 1] = y + offsets.y;
      vertices[index + 2] = z + offsets.z;
    }
  }
}

function mod(value: number, divisor: number): number {
  if (divisor === 0) return 0;
  return ((value % divisor) + divisor) % divisor;
}

function disposeChildren(group: THREE.Group): void {
  for (const child of [...group.children]) {
    group.remove(child);
    disposeObject(child);
  }
}

function disposeObject(node: THREE.Object3D): void {
  node.traverse((entry) => {
    if (!(entry instanceof THREE.Mesh)) {
      return;
    }
    entry.geometry.dispose();
    const material = entry.material;
    if (Array.isArray(material)) {
      for (const part of material) {
        part.dispose();
      }
      return;
    }
    material.dispose();
  });
}
